// src/modules/Orders/SaleOrderPage.js - WITH PALLET TYPE SELECTION AND COMBOBOX
// VERSION: 4.1 - Added RETURNED_FOR_ANALYSIS support for edit/delete

import React, { useState, useMemo, useEffect, useCallback } from 'react';
import useFirestore from '../../hooks/useFirestore';
import useAuth from '../../hooks/useAuth';
import usePagePermissions from '../../hooks/usePagePermissions';
import { doc, getDoc, getDocs, collection, addDoc, query, where } from 'firebase/firestore';
import { db } from '../../firebaseConfig';
import { useNotifications } from '../../contexts/NotificationContext';
import { getModuleConfig } from '../../config/notificationConfig';
import DeleteModal from '../../components/common/DeleteModal/DeleteModal';
import Toast from '../../components/common/Toast/Toast';
import SearchBar from '../../components/common/SearchBar/SearchBar';
import Pagination from '../../components/common/Pagination/Pagination';
import TableSkeleton from '../../components/common/TableSkeleton/TableSkeleton';
import EmptyState from '../../components/common/EmptyState/EmptyState';
import PageHeader from '../../components/common/PageHeader/PageHeader';
import StatusBadge from '../../components/common/StatusBadge/StatusBadge';
import ViewOrderModal from '../../components/common/ViewOrderModal/ViewOrderModal';
import TrackingModal from '../../components/common/TrackingModal/TrackingModal';
import ConfirmOrderModal from '../../components/common/ConfirmOrderModal/ConfirmOrderModal';
import ComboBox from '../../components/common/ComboBox/ComboBox';
import styles from './Orders.module.css';

// React Icons
import {
    FiX,
    FiSave,
    FiEdit,
    FiTrash2,
    FiArrowLeft,
    FiArrowRight,
    FiRefreshCw,
    FiCheck,
    FiAlertCircle,
    FiInfo,
    FiFileText,
    FiSearch,
    FiPackage,
    FiClipboard,
    FiMapPin,
    FiList,
    FiEye,
    FiPlus,
    FiTruck,
    FiDollarSign,
    FiTrendingUp,
    FiCheckCircle,
    FiClock,
    FiPrinter,
    FiBox,
    FiCreditCard,
    FiTag,
    FiCalendar,
    FiSettings,
    FiUser,
    FiMaximize
} from 'react-icons/fi';
import {
    MdBusiness,
    MdPerson,
    MdPhone,
    MdEmail,
    MdLocationCity
} from 'react-icons/md';
import { FaClipboardList, FaShip } from 'react-icons/fa';

// ============================================
// 1. CONSTANTES E DEFINIÇÕES
// ============================================
const SALES_ORDERS_COLLECTION = 'sales-orders';
const CLIENTS_COLLECTION = 'clients';
const PRODUCTS_COLLECTION = 'products';
const ACCOUNT_EXECUTIVES_COLLECTION = 'account-executives';
const PAYMENT_METHODS_COLLECTION = 'parameters/types/documento-pagamento';
const BILLING_CONDITIONS_COLLECTION = 'parameters/data/billing-conditions';
const NCM_COLLECTION = 'parameters/data/ncm';
const BOX_TYPE_COLLECTION = 'parameters/types/tipo-caixa';
const PALLET_TYPE_COLLECTION = 'parameters/types/tipo-palete';

const DESTINATION_OPTIONS = [
    { value: 'industrial', label: 'Industrial' },
    { value: 'commercial', label: 'Comercial' },
    { value: 'residential', label: 'Residencial' },
    { value: 'rural', label: 'Rural' }
];

const ITEMS_PER_PAGE = 10;

// Status do Pedido de Venda
const ORDER_STATUS = {
    DRAFT: 'draft',
    PENDING: 'pending',
    APPROVED: 'approved',
    IN_PRODUCTION: 'in_production',
    SHIPPED: 'shipped',
    DELIVERED: 'delivered',
    CANCELLED: 'cancelled',
    RETURNED_FOR_ANALYSIS: 'returned_for_analysis'
};

const ORDER_STATUS_LABELS = {
    [ORDER_STATUS.DRAFT]: { label: 'Rascunho', className: 'statusBadgeDraft' },
    [ORDER_STATUS.PENDING]: { label: 'Pendente Aprovação', className: 'statusBadgePending' },
    [ORDER_STATUS.APPROVED]: { label: 'Aprovado', className: 'statusBadgeApproved' },
    [ORDER_STATUS.IN_PRODUCTION]: { label: 'Em Produção', className: 'statusBadgeInfo' },
    [ORDER_STATUS.SHIPPED]: { label: 'Enviado', className: 'statusBadgeInfo' },
    [ORDER_STATUS.DELIVERED]: { label: 'Entregue', className: 'statusBadgeCompleted' },
    [ORDER_STATUS.CANCELLED]: { label: 'Cancelado', className: 'statusBadgeCancelled' },
    [ORDER_STATUS.RETURNED_FOR_ANALYSIS]: { label: 'Retornado para Análise', className: 'statusBadgeWarning' }
};

// Helper functions
const formatCurrencyUSD = (value) => {
    if (!value && value !== 0) return '$ 0.00';
    return `$ ${Number(value).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};
const formatToBrazilianDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('pt-BR');
};

const parseBrazilianDate = (dateString) => {
    if (!dateString) return '';
    const parts = dateString.split('/');
    if (parts.length === 3) {
        const day = parts[0].padStart(2, '0');
        const month = parts[1].padStart(2, '0');
        const year = parts[2];
        return `${year}-${month}-${day}`;
    }
    return dateString;
};

const formatCurrency = (value) => {
    if (!value && value !== 0) return 'R$ 0,00';
    return `R$ ${Number(value).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`;
};

// ============================================
// 2. MAIN COMPONENT
// ============================================
const SaleOrderPage = () => {
    const can = usePagePermissions('pedido_de_venda');

    // ============================================
    // 2.1 ESTADOS PRINCIPAIS
    // ============================================
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isViewModalOpen, setIsViewModalOpen] = useState(false);
    const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
    const [isTrackingModalOpen, setIsTrackingModalOpen] = useState(false);
    const [selectedOrder, setSelectedOrder] = useState(null);
    const [editingItem, setEditingItem] = useState(null);
    const [formStatus, setFormStatus] = useState({ message: '', type: '' });
    const [formLoading, setFormLoading] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [toasts, setToasts] = useState([]);
    const [deleteModal, setDeleteModal] = useState(null);
    const [confirmOrderLoading, setConfirmOrderLoading] = useState(false);

    // ============================================
    // 2.1.5 BOX COMPLETION WARNING STATE
    // ============================================
    const [boxCompletionWarnings, setBoxCompletionWarnings] = useState({});

    // Helper: Check if a quantity fills complete boxes - ROUNDS UP
    const getBoxCompletionStatus = useCallback((quantity, unitsPerBox) => {
        if (!quantity || !unitsPerBox || unitsPerBox <= 0) return null;

        const qty = parseFloat(quantity) || 0;
        const units = parseFloat(unitsPerBox) || 0;

        if (qty === 0 || units === 0) return null;

        const boxesNeeded = qty / units;
        const isComplete = Number.isInteger(boxesNeeded) && boxesNeeded > 0;
        const completeBoxes = Math.floor(boxesNeeded);
        const remainingUnits = qty - (completeBoxes * units);
        const remainingPercentage = units > 0 ? (remainingUnits / units) * 100 : 0;

        // ROUND UP TO NEXT COMPLETE BOX
        const boxesToComplete = Math.ceil(boxesNeeded);
        const recommendedQuantity = boxesToComplete * units;
        const additionalUnitsNeeded = recommendedQuantity - qty;

        return {
            boxesNeeded,
            isComplete,
            completeBoxes,
            remainingUnits,
            remainingPercentage,
            unitsPerBox: units,
            boxesToComplete,
            recommendedQuantity,
            additionalUnitsNeeded
        };
    }, []);

    // Helper: Get warning severity based on remaining percentage
    const getWarningSeverity = useCallback((remainingPercentage) => {
        if (remainingPercentage === 0) return 'none';
        if (remainingPercentage >= 75) return 'critical';
        if (remainingPercentage >= 50) return 'warning';
        if (remainingPercentage >= 25) return 'info';
        return 'minor';
    }, []);

    // Estados do Formulário - Identificação do Cliente
    const [selectedClientId, setSelectedClientId] = useState('');
    const [selectedAccountExecutiveId, setSelectedAccountExecutiveId] = useState('');
    const [billingAddress, setBillingAddress] = useState('');
    const [deliveryAddress, setDeliveryAddress] = useState('');
    const [selectedContactId, setSelectedContactId] = useState('');
    const [contactPhone, setContactPhone] = useState('');
    const [clientOrderNumber, setClientOrderNumber] = useState('');
    const [clientDiscountPercent, setClientDiscountPercent] = useState('');
    const [discountTicket, setDiscountTicket] = useState({ type: 'amount', value: '' });
    const [destination, setDestination] = useState('');
    const [shippingMethod, setShippingMethod] = useState('sea');

    // NEW: Pallet Type Selection
    const [selectedPalletTypeId, setSelectedPalletTypeId] = useState('');
    const [allPalletTypes, setAllPalletTypes] = useState([]);
    const [loadingPalletTypes, setLoadingPalletTypes] = useState(false);

    // NOVOS ESTADOS PARA IMPORTAÇÃO
    const [useImporter, setUseImporter] = useState(false);
    const [selectedImporterId, setSelectedImporterId] = useState('');
    const [importerCompanyName, setImporterCompanyName] = useState('');
    const [importerAddress, setImporterAddress] = useState('');
    const [importerContact, setImporterContact] = useState('');
    const [importerPhone, setImporterPhone] = useState('');

    // Estados - Produtos
    const [orderItems, setOrderItems] = useState([]);
    const [selectedProductId, setSelectedProductId] = useState('');
    const [isBonusOrder, setIsBonusOrder] = useState(false);
    const [isSampleOrder, setIsSampleOrder] = useState(false);

    // Helper: Auto-fix all incomplete box quantities (round up)
    const handleAutoFixQuantities = useCallback(() => {
        if (orderItems.length === 0) {
            addToast('Nenhum item para ajustar.', 'Warning');
            return;
        }

        let fixedCount = 0;
        const updatedItems = orderItems.map(item => {
            const unitsPerBox = parseFloat(item.unitsPerBox) || 0;
            const quantity = parseFloat(item.quantity) || 0;

            if (unitsPerBox > 0 && quantity > 0) {
                const status = getBoxCompletionStatus(quantity, unitsPerBox);
                if (status && !status.isComplete) {
                    const newQuantity = status.recommendedQuantity;
                    const unitPrice = parseFloat(item.unitPrice) || 0;
                    const newTotal = (newQuantity * unitPrice).toFixed(2);
                    fixedCount++;
                    return {
                        ...item,
                        quantity: newQuantity.toString(),
                        total: newTotal
                    };
                }
            }
            return item;
        });

        if (fixedCount > 0) {
            setOrderItems(updatedItems);
            addToast(`${fixedCount} item(ns) ajustado(s) para completar todas as caixas! 📦`, 'Success');
        } else {
            addToast('Todos os itens já estão com caixas completas! ✅', 'Info');
        }
    }, [orderItems, getBoxCompletionStatus]);

    // Estados - Condições Comerciais
    const [estimatedIcms, setEstimatedIcms] = useState('');
    const [estimatedIcmsSt, setEstimatedIcmsSt] = useState('');
    const [estimatedFcpSt, setEstimatedFcpSt] = useState('');
    const [estimatedIpi, setEstimatedIpi] = useState('');
    const [commissionPercent, setCommissionPercent] = useState('');
    const [commissionValue, setCommissionValue] = useState('');
    const [commissionType, setCommissionType] = useState('percent');
    const [commercialNotes, setCommercialNotes] = useState('');
    const [withoutSeals, setWithoutSeals] = useState(false);

    // Estados - Plano Recebimento
    const [selectedPaymentMethodId, setSelectedPaymentMethodId] = useState('');
    const [selectedBillingConditionId, setSelectedBillingConditionId] = useState('');

    // Estados - Plano Entrega
    const [deliveryDate, setDeliveryDate] = useState('');
    const [deliveryDateDisplay, setDeliveryDateDisplay] = useState('');

    // Estados - Dados para Produção
    const [productionNotes, setProductionNotes] = useState('');

    // Cache para box types
    const [boxTypesCache, setBoxTypesCache] = useState([]);

    // ============================================
    // 2.2 HOOKS DE DADOS
    // ============================================
    const { currentUser } = useAuth();
    const { addNotification } = useNotifications();

    const {
        data: orders,
        loading: ordersLoading,
        addDocument,
        updateDocument,
        deleteDocument,
        refreshData: refreshOrders
    } = useFirestore(SALES_ORDERS_COLLECTION, 'createdAt', true);

    const { data: clients, loading: clientsLoading } = useFirestore(CLIENTS_COLLECTION, 'razaoSocial');
    const { data: products, loading: productsLoading } = useFirestore(PRODUCTS_COLLECTION, 'code');
    const { data: accountExecutives, loading: executivesLoading } = useFirestore(ACCOUNT_EXECUTIVES_COLLECTION, 'nome');
    const { data: paymentMethods, loading: paymentMethodsLoading } = useFirestore(PAYMENT_METHODS_COLLECTION, 'nome');
    const { data: billingConditions, loading: billingConditionsLoading } = useFirestore(BILLING_CONDITIONS_COLLECTION, 'name');
    const { data: ncmList, loading: ncmListLoading } = useFirestore(NCM_COLLECTION);

    // Fetch box types
    useEffect(() => {
        const fetchBoxTypes = async () => {
            try {
                const boxTypesRef = collection(db, BOX_TYPE_COLLECTION);
                const boxTypesSnap = await getDocs(boxTypesRef);
                const boxTypesData = boxTypesSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
                setBoxTypesCache(boxTypesData);
            } catch (error) {
                console.error('Erro ao buscar tipos de caixa:', error);
            }
        };
        fetchBoxTypes();
    }, []);

    // Fetch pallet types
    useEffect(() => {
        const fetchPalletTypes = async () => {
            setLoadingPalletTypes(true);
            try {
                const palletRef = collection(db, PALLET_TYPE_COLLECTION);
                const snapshot = await getDocs(palletRef);
                const pallets = [];
                snapshot.forEach(docSnap => {
                    pallets.push({
                        id: docSnap.id,
                        ...docSnap.data()
                    });
                });
                // Sort by name
                pallets.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
                setAllPalletTypes(pallets);
            } catch (error) {
                console.error('Erro ao buscar tipos de palete:', error);
            } finally {
                setLoadingPalletTypes(false);
            }
        };
        fetchPalletTypes();
    }, []);

    // Responsável
    const responsible = useMemo(() => {
        if (currentUser) {
            return currentUser.nome || currentUser.email?.split('@')[0] || 'Usuário';
        }
        return 'Usuário';
    }, [currentUser]);

    // Create a map for quick NCM lookup by code
    const ncmMap = useMemo(() => {
        if (!ncmList) return new Map();
        const map = new Map();
        ncmList.forEach(ncm => {
            if (ncm.ncmCode) {
                map.set(ncm.ncmCode, ncm);
            }
        });
        return map;
    }, [ncmList]);

    // Buscar informações do cliente selecionado
    const selectedClient = useMemo(() => {
        if (!selectedClientId || !clients) return null;
        return clients.find(c => c.id === selectedClientId);
    }, [selectedClientId, clients]);

    // Extract all contacts from all addresses
    const clientContacts = useMemo(() => {
        if (!selectedClient) return [];
        const addresses = selectedClient.addresses || [];
        const allContacts = [];

        addresses.forEach((addr, addrIndex) => {
            if (addr.contacts && Array.isArray(addr.contacts)) {
                addr.contacts.forEach((contact, contactIndex) => {
                    allContacts.push({
                        id: `contact-${addrIndex}-${contactIndex}`,
                        name: contact.nome || '',
                        email: contact.email || '',
                        cargo: contact.cargo || '',
                        telefone: contact.telefone || '',
                        addressType: addr.tipo || 'Endereço',
                        address: `${addr.logradouro || ''}, ${addr.numero || ''}`
                    });
                });
            }
        });

        return allContacts;
    }, [selectedClient]);

    // Buscar endereços do cliente com IDs gerados
    const clientAddresses = useMemo(() => {
        if (!selectedClient) return [];
        return (selectedClient.addresses || []).map((addr, index) => ({
            ...addr,
            id: addr.id || `addr-${index}`,
            formattedAddress: `${addr.logradouro || ''}, ${addr.numero || ''}${addr.complemento ? `, ${addr.complemento}` : ''} - ${addr.bairro || ''} - ${addr.cidade || ''}/${addr.uf || ''}`
        }));
    }, [selectedClient]);

    // NOVO: Buscar endereços do importador
    const importerAddresses = useMemo(() => {
        if (!selectedClient) return [];
        return (selectedClient.addresses || [])
            .filter(addr => addr.isImporter === true)
            .map((addr, index) => ({
                ...addr,
                id: addr.id || `importer-addr-${index}`,
                formattedAddress: `${addr.logradouro || ''}, ${addr.numero || ''}${addr.complemento ? `, ${addr.complemento}` : ''} - ${addr.bairro || ''} - ${addr.cidade || ''}/${addr.uf || ''}`,
                importerCompanyName: addr.importerCompanyName || ''
            }));
    }, [selectedClient]);

    // NOVO: Buscar contatos do importador
    const importerContacts = useMemo(() => {
        if (!selectedClient || !useImporter) return [];
        const addresses = selectedClient.addresses || [];
        const importerAddrs = addresses.filter(addr => addr.isImporter === true);
        const allContacts = [];

        importerAddrs.forEach((addr, addrIndex) => {
            if (addr.contacts && Array.isArray(addr.contacts)) {
                addr.contacts.forEach((contact, contactIndex) => {
                    allContacts.push({
                        id: `importer-contact-${addrIndex}-${contactIndex}`,
                        name: contact.nome || '',
                        email: contact.email || '',
                        cargo: contact.cargo || '',
                        telefone: contact.telefone || '',
                        addressType: addr.tipo || 'Importador',
                        address: `${addr.logradouro || ''}, ${addr.numero || ''}`,
                        importerCompanyName: addr.importerCompanyName || ''
                    });
                });
            }
        });

        return allContacts;
    }, [selectedClient, useImporter]);

    // Opções de produtos com NCM info e box types - formatted for ComboBox
    const productOptions = useMemo(() => {
        if (!products) return [];
        return products.map(product => {
            const technical = product.technical || {};

            // Extract available box types from product
            let availableBoxTypes = [];
            const boxTypeConfigs = technical.boxTypeConfigs || [];
            const boxWeightsMap = technical.boxWeights || {};

            if (boxTypeConfigs.length > 0) {
                availableBoxTypes = boxTypeConfigs.map(config => {
                    const box = boxTypesCache.find(b => b.id === config.boxTypeId);
                    return {
                        boxTypeId: config.boxTypeId,
                        unitsPerBox: config.unitsPerBox || 1,
                        boxName: box?.name || config.boxTypeId,
                        boxDetails: box || null,
                        boxNetWeight: boxWeightsMap[config.boxTypeId]?.boxNetWeight || 0,
                        boxGrossWeight: boxWeightsMap[config.boxTypeId]?.boxGrossWeight || 0,
                        boxVolume: boxWeightsMap[config.boxTypeId]?.boxVolume || 0
                    };
                });
            } else if (technical.boxTypeId) {
                // Legacy: single box type
                const box = boxTypesCache.find(b => b.id === technical.boxTypeId);
                availableBoxTypes = [{
                    boxTypeId: technical.boxTypeId,
                    unitsPerBox: technical.unitsPerBox || 1,
                    boxName: box?.name || technical.boxTypeId,
                    boxDetails: box || null,
                    boxNetWeight: technical.boxNetWeight || 0,
                    boxGrossWeight: technical.boxGrossWeight || 0,
                    boxVolume: 0
                }];
            }

            const hasBoxTypes = availableBoxTypes.length > 0;
            const boxInfo = hasBoxTypes ? ` (${availableBoxTypes.length} caixas)` : ' (sem caixa)';

            return {
                id: product.id,
                value: product.id,
                label: `[${product.code}] ${product.commercialName}${product.ncmCode ? ` (NCM: ${product.ncmCode})` : ''}${boxInfo}`,
                product: product,
                ncmCode: product.ncmCode,
                availableBoxTypes: availableBoxTypes
            };
        });
    }, [products, boxTypesCache]);

    // Client options formatted for ComboBox
    const clientOptions = useMemo(() => {
        if (!clients) return [];
        return clients.map(client => ({
            id: client.id,
            value: client.id,
            label: `${client.codigo || ''} - ${client.razaoSocial}`,
            client: client
        }));
    }, [clients]);

    // Opções de executivos de contas
    const executiveOptions = useMemo(() => {
        if (!accountExecutives) return [];
        const activeExecutives = accountExecutives.filter(exec => exec.situacao === 'ativo');
        return activeExecutives.map(exec => ({
            value: exec.id,
            label: `${exec.nome}${exec.emailUsuario ? ` (${exec.emailUsuario})` : ''}`,
            executive: exec,
            comissaoPadrao: exec.comissaoPadrao || 0
        }));
    }, [accountExecutives]);

    // Opções de formas de pagamento
    const paymentMethodOptions = useMemo(() => {
        if (!paymentMethods) return [];
        return paymentMethods.map(method => ({
            value: method.id,
            label: method.nome,
            method: method
        }));
    }, [paymentMethods]);

    // Opções de condições de faturamento
    const billingConditionOptions = useMemo(() => {
        if (!billingConditions) return [];
        return billingConditions.map(condition => ({
            value: condition.id,
            label: `${condition.name} (${condition.days} dias)`,
            days: condition.days
        }));
    }, [billingConditions]);

    // ============================================
    // 2.3 NOTIFICATION FUNCTIONS
    // ============================================

    /**
     * Notifica os aprovadores sobre um novo pedido de venda aguardando aprovação
     */
    const notifyApprovers = async (orderData, orderId) => {
        try {
            // Buscar usuários com permissão para aprovar pedidos de venda
            const usersSnapshot = await getDocs(collection(db, 'users'));
            const targetUsers = [];

            usersSnapshot.forEach(doc => {
                const userData = doc.data();
                const roles = Array.isArray(userData.role) ? userData.role : [userData.role];

                // Usuários com Diretoria, Diretoria (Admin) ou Financeiro podem aprovar
                if (roles.some(r => r === 'Diretoria' || r === 'Diretoria (Admin)' || r === 'Financeiro')) {
                    targetUsers.push({
                        uid: doc.id,
                        ...userData
                    });
                }
            });

            console.log(`📢 Enviando notificações de aprovação para ${targetUsers.length} usuários`);

            const totalValue = orderData.calculatedValues?.total || 0;
            const config = getModuleConfig('pedido_de_venda');

            const baseNotification = {
                type: 'sales_order_pending_approval',
                module: 'pedido_de_venda',
                moduleIcon: config?.icon || '📄',
                moduleColor: config?.color || '#2980b9',
                moduleName: config?.name || 'Pedido de Venda',
                title: '📋 Novo Pedido de Venda Aguardando Aprovação',
                fullMessage: `Detalhes do pedido:\n• Código: ${orderData.code}\n• Cliente: ${orderData.clientName || 'N/A'}\n• Valor Total: ${formatCurrency(totalValue)}\n• Itens: ${orderData.items?.length || 0}\n• Responsável: ${orderData.responsible || 'N/A'}`,
                timestamp: new Date().toISOString(),
                read: false,
                priority: 'Alta',
                route: `/pedidos/pedido-de-venda`,
                data: {
                    orderId: orderId,
                    orderCode: orderData.code,
                    clientName: orderData.clientName,
                    totalValue: totalValue
                }
            };

            for (const user of targetUsers) {
                if (user.uid === currentUser?.uid) continue;

                addNotification({
                    ...baseNotification,
                    id: `so_pending_${orderId}_${user.uid}_${Date.now()}`,
                    message: `${user.nome || 'Usuário'}, ${orderData.responsible || 'Alguém'} criou um novo pedido de venda ${orderData.code} - ${orderData.clientName || 'Cliente'} - ${formatCurrency(totalValue)}`,
                    userId: user.uid
                });

                try {
                    await addDoc(collection(db, 'notifications'), {
                        userId: user.uid,
                        ...baseNotification,
                        id: `so_pending_${orderId}_${user.uid}_${Date.now()}`,
                        message: `${user.nome || 'Usuário'}, ${orderData.responsible || 'Alguém'} criou um novo pedido de venda ${orderData.code} - ${orderData.clientName || 'Cliente'} - ${formatCurrency(totalValue)}`,
                        createdAt: new Date()
                    });
                } catch (err) {
                    console.warn('Erro ao salvar notificação:', err);
                }
            }
        } catch (error) {
            console.error('❌ Erro ao notificar aprovadores:', error);
        }
    };

    /**
     * Notifica o criador sobre a aprovação/rejeição do pedido
     */
    const notifyOrderCreator = async (orderId, status, orderCode) => {
        try {
            const orderDoc = await getDoc(doc(db, 'sales-orders', orderId));
            if (!orderDoc.exists()) return;

            const orderData = orderDoc.data();
            const creatorName = orderData.responsible;

            if (!creatorName) return;

            const usersSnapshot = await getDocs(collection(db, 'users'));
            let creatorUser = null;

            usersSnapshot.forEach(doc => {
                const data = doc.data();
                if (data.nome === creatorName || data.email === creatorName) {
                    creatorUser = { uid: doc.id, ...data };
                }
            });

            if (!creatorUser || creatorUser.uid === currentUser?.uid) return;

            const isApproved = status === ORDER_STATUS.APPROVED;
            const config = getModuleConfig('pedido_de_venda');

            const notification = {
                id: `so_${status}_${orderId}_${Date.now()}`,
                type: isApproved ? 'sales_order_approved' : 'sales_order_rejected',
                module: 'pedido_de_venda',
                moduleIcon: config?.icon || '📄',
                moduleColor: config?.color || '#2980b9',
                moduleName: config?.name || 'Pedido de Venda',
                title: isApproved ? '✅ Pedido Aprovado' : '❌ Pedido Rejeitado',
                message: `Seu pedido ${orderCode} foi ${isApproved ? 'aprovado' : 'rejeitado'} por ${currentUser?.nome || 'Aprovador'}`,
                fullMessage: `Detalhes:\n• Pedido: ${orderCode}\n• Status: ${isApproved ? 'Aprovado' : 'Rejeitado'}\n• Aprovado por: ${currentUser?.nome || 'Aprovador'}`,
                timestamp: new Date().toISOString(),
                read: false,
                priority: isApproved ? 'Alta' : 'Média',
                route: `/pedidos/pedido-de-venda`,
                data: {
                    orderId: orderId,
                    orderCode: orderCode,
                    status: status,
                    approvedBy: currentUser?.nome || 'Aprovador'
                },
                userId: creatorUser.uid
            };

            addNotification(notification);

            try {
                await addDoc(collection(db, 'notifications'), {
                    userId: creatorUser.uid,
                    ...notification,
                    createdAt: new Date()
                });
            } catch (err) {
                console.warn('Erro ao salvar notificação:', err);
            }
        } catch (error) {
            console.error('❌ Erro ao notificar criador:', error);
        }
    };

    // ============================================
    // 2.4 TOAST FUNCTIONS
    // ============================================
    const addToast = (message, type) => {
        const id = Date.now();
        setToasts(prev => [...prev, { id, message, type }]);
        setTimeout(() => {
            setToasts(prev => prev.filter(toast => toast.id !== id));
        }, 5000);
    };

    // ============================================
    // 2.5 FUNÇÕES AUXILIARES
    // ============================================

    // Gerar código automaticamente com prefixo PV-
    const generateOrderCode = useCallback(async () => {
        // If no orders exist, start from PV-1333
        if (!orders || orders.length === 0) {
            return 'PV-1333';
        }

        let maxNumber = 0;

        orders.forEach(order => {
            if (!order.code) return;
            // Extract numeric part from the code (handles both formats)
            const numMatch = order.code.match(/^PV-?(\d+)/);
            if (numMatch) {
                const num = parseInt(numMatch[1], 10);
                if (!isNaN(num) && num > maxNumber) {
                    maxNumber = num;
                }
            } else {
                // If code is just a number (legacy format)
                const num = parseInt(order.code, 10);
                if (!isNaN(num) && num > maxNumber) {
                    maxNumber = num;
                }
            }
        });

        // If maxNumber is 1332 or less, start from 1333
        const nextNumber = Math.max(maxNumber + 1, 1333);
        return `PV-${nextNumber}`;
    }, [orders]);

    // Function to get NCM configuration for a product and auto-fill tax fields
    const applyNcmTaxConfig = useCallback((ncmConfig) => {
        if (!ncmConfig) return;

        if (ncmConfig.aliquotaIcmsProprio !== undefined && ncmConfig.aliquotaIcmsProprio > 0) {
            setEstimatedIcms(ncmConfig.aliquotaIcmsProprio.toString());
        }
        if (ncmConfig.aliquotaIcmsSTOutraUF !== undefined && ncmConfig.aliquotaIcmsSTOutraUF > 0) {
            setEstimatedIcmsSt(ncmConfig.aliquotaIcmsSTOutraUF.toString());
        }
        if (ncmConfig.percentualFundoPobreza !== undefined && ncmConfig.percentualFundoPobreza > 0) {
            setEstimatedFcpSt(ncmConfig.percentualFundoPobreza.toString());
        }
        if (ncmConfig.aliquotaIpiSaida !== undefined && ncmConfig.aliquotaIpiSaida > 0) {
            setEstimatedIpi(ncmConfig.aliquotaIpiSaida.toString());
        }
    }, []);

    // Handle contact selection
    const handleContactChange = (contactId) => {
        setSelectedContactId(contactId);
        const selected = clientContacts.find(c => c.id === contactId);
        if (selected) {
            setContactPhone(selected.telefone || '');
        }
    };

    // NOVO: Handle importer selection
    const handleImporterChange = (addressId) => {
        setSelectedImporterId(addressId);
        const selected = importerAddresses.find(addr => addr.id === addressId);
        if (selected) {
            setImporterAddress(selected.formattedAddress);
            setImporterCompanyName(selected.importerCompanyName || '');
            const contact = importerContacts.find(c => c.address === selected.formattedAddress);
            if (contact) {
                setImporterContact(contact.name || '');
                setImporterPhone(contact.telefone || '');
            } else {
                setImporterContact('');
                setImporterPhone('');
            }
        }
    };

    // NOVO: Handle importer contact change
    const handleImporterContactChange = (contactId) => {
        const selected = importerContacts.find(c => c.id === contactId);
        if (selected) {
            setImporterContact(selected.name || '');
            setImporterPhone(selected.telefone || '');
            setImporterCompanyName(selected.importerCompanyName || '');
            const address = importerAddresses.find(addr => addr.formattedAddress === selected.address);
            if (address) {
                setImporterAddress(address.formattedAddress);
                setSelectedImporterId(address.id);
            }
        }
    };

    // ============================================
    // 2.6 EFFECTS
    // ============================================

    useEffect(() => {
        if (selectedClient) {
            const addresses = selectedClient.addresses || [];
            const mainAddress = addresses.find(addr => addr.isMain) || addresses[0];

            if (mainAddress) {
                const addressStr = `${mainAddress.logradouro || ''}, ${mainAddress.numero || ''}${mainAddress.complemento ? `, ${mainAddress.complemento}` : ''} - ${mainAddress.bairro || ''} - ${mainAddress.cidade || ''}/${mainAddress.uf || ''}`;
                setBillingAddress(addressStr);
            }

            if (!deliveryAddress && mainAddress) {
                const addressStr = `${mainAddress.logradouro || ''}, ${mainAddress.numero || ''} - ${mainAddress.bairro || ''} - ${mainAddress.cidade || ''}/${mainAddress.uf || ''}`;
                setDeliveryAddress(addressStr);
            }

            const hasImporter = addresses.some(addr => addr.isImporter === true);
            if (!hasImporter && useImporter) {
                setUseImporter(false);
                setImporterAddress('');
                setImporterCompanyName('');
                setImporterContact('');
                setImporterPhone('');
            }
        }
    }, [selectedClient]);

    useEffect(() => {
        if (selectedClient) {
            if (selectedClient.accountExecutiveId) {
                setSelectedAccountExecutiveId(selectedClient.accountExecutiveId);
            } else {
                setSelectedAccountExecutiveId('');
            }
        }
    }, [selectedClient]);

    useEffect(() => {
        if (orderItems.length === 0) {
            setEstimatedIcms('');
            setEstimatedIcmsSt('');
            setEstimatedFcpSt('');
            setEstimatedIpi('');
        }
    }, [orderItems.length]);

    useEffect(() => {
        if (isModalOpen && !editingItem) {
            const today = new Date();
            const deliveryDateObj = new Date(today);
            deliveryDateObj.setDate(today.getDate() + 7);
            const isoDate = deliveryDateObj.toISOString().split('T')[0];
            setDeliveryDate(isoDate);
            setDeliveryDateDisplay(formatToBrazilianDate(isoDate));
        }
    }, [isModalOpen, editingItem]);

    // Gerar código automaticamente ao abrir o modal para novo pedido
    useEffect(() => {
        if (isModalOpen && !editingItem) {
            const generateCode = async () => {
                const code = await generateOrderCode();
                setEditingItem(prev => prev ? prev : { code });
            };
            generateCode();
        }
    }, [isModalOpen, editingItem, generateOrderCode]);

    // Real-time status sync
    useEffect(() => {
        const handleVisibilityChange = () => {
            if (!document.hidden && refreshOrders) {
                refreshOrders();
            }
        };

        const intervalId = setInterval(() => {
            if (refreshOrders && !document.hidden) {
                refreshOrders();
            }
        }, 30000);

        document.addEventListener('visibilitychange', handleVisibilityChange);

        return () => {
            clearInterval(intervalId);
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
    }, [refreshOrders]);

    // Calcular valores
    const calculatedValues = useMemo(() => {
        const itemsTotal = orderItems.reduce((sum, item) => {
            const qty = parseFloat(item.quantity) || 0;
            const price = parseFloat(item.unitPrice) || 0;
            const isBonusItem = item.isBonus || false;
            if (!isBonusItem) {
                return sum + (qty * price);
            }
            return sum;
        }, 0);

        const bonusTotal = orderItems.reduce((sum, item) => {
            const qty = parseFloat(item.quantity) || 0;
            const price = parseFloat(item.unitPrice) || 0;
            const isBonusItem = item.isBonus || false;
            if (isBonusItem) {
                return sum + (qty * price);
            }
            return sum;
        }, 0);

        const clientDiscount = (parseFloat(clientDiscountPercent) || 0);
        const discountAmount = itemsTotal * (clientDiscount / 100);

        let ticketDiscountAmount = 0;
        if (discountTicket.type === 'amount') {
            ticketDiscountAmount = parseFloat(discountTicket.value) || 0;
        } else {
            ticketDiscountAmount = itemsTotal * ((parseFloat(discountTicket.value) || 0) / 100);
        }

        const subtotalWithDiscounts = itemsTotal - discountAmount - ticketDiscountAmount;

        const icms = parseFloat(estimatedIcms) || 0;
        const icmsSt = parseFloat(estimatedIcmsSt) || 0;
        const fcpSt = parseFloat(estimatedFcpSt) || 0;
        const ipi = parseFloat(estimatedIpi) || 0;

        let commissionTotal = 0;
        if (commissionType === 'percent') {
            const percent = parseFloat(commissionPercent) || 0;
            commissionTotal = itemsTotal * (percent / 100);
        } else {
            commissionTotal = parseFloat(commissionValue) || 0;
        }

        const total = subtotalWithDiscounts + icms + icmsSt + fcpSt + ipi;

        return {
            itemsTotal: itemsTotal.toFixed(2),
            bonusTotal: bonusTotal.toFixed(2),
            discountAmount: discountAmount.toFixed(2),
            ticketDiscountAmount: ticketDiscountAmount.toFixed(2),
            subtotalWithDiscounts: subtotalWithDiscounts.toFixed(2),
            icms: icms.toFixed(2),
            icmsSt: icmsSt.toFixed(2),
            fcpSt: fcpSt.toFixed(2),
            ipi: ipi.toFixed(2),
            commissionTotal: commissionTotal.toFixed(2),
            total: total.toFixed(2)
        };
    }, [orderItems, clientDiscountPercent, discountTicket, estimatedIcms, estimatedIcmsSt, estimatedFcpSt, estimatedIpi, commissionPercent, commissionValue, commissionType]);

    // Handler for delivery date change with Brazilian format
    const handleDeliveryDateChange = (e) => {
        const brazilianDate = e.target.value;
        setDeliveryDateDisplay(brazilianDate);
        const isoDate = parseBrazilianDate(brazilianDate);
        setDeliveryDate(isoDate);
    };

    // ============================================
    // 2.7 HANDLERS DO FORMULÁRIO
    // ============================================

    const resetForm = () => {
        setEditingItem(null);
        setIsModalOpen(false);
        setSelectedClientId('');
        setSelectedAccountExecutiveId('');
        setBillingAddress('');
        setDeliveryAddress('');
        setSelectedContactId('');
        setContactPhone('');
        setClientOrderNumber('');
        setClientDiscountPercent('');
        setDiscountTicket({ type: 'amount', value: '' });
        setDestination('');
        setShippingMethod('sea');
        setSelectedPalletTypeId('');
        setOrderItems([]);
        setSelectedProductId('');
        setIsBonusOrder(false);
        setIsSampleOrder(false);
        setEstimatedIcms('');
        setEstimatedIcmsSt('');
        setEstimatedFcpSt('');
        setEstimatedIpi('');
        setCommissionPercent('');
        setCommissionValue('');
        setCommissionType('percent');
        setCommercialNotes('');
        setWithoutSeals(false);
        setSelectedPaymentMethodId('');
        setSelectedBillingConditionId('');
        setDeliveryDate('');
        setDeliveryDateDisplay('');
        setProductionNotes('');
        setFormStatus({ message: '', type: '' });
        setUseImporter(false);
        setSelectedImporterId('');
        setImporterCompanyName('');
        setImporterAddress('');
        setImporterContact('');
        setImporterPhone('');
        setBoxCompletionWarnings({});
    };

    // ============================================
    // 2.8 HANDLERS WITH NOTIFICATIONS
    // ============================================

    const handleUpdateOrderStatus = async (orderId, newStatus, description) => {
        if (!orderId) {
            addToast('ID do pedido não encontrado', 'Error');
            return;
        }

        setFormLoading(true);
        try {
            const order = orders.find(o => o.id === orderId);
            const existingHistory = order?.trackingHistory || [];

            const getStatusLabel = (status) => {
                const statusConfig = ORDER_STATUS_LABELS[status] || ORDER_STATUS_LABELS[ORDER_STATUS.DRAFT];
                return statusConfig.label;
            };

            const newHistoryEntry = {
                date: new Date().toLocaleDateString('pt-BR'),
                status: getStatusLabel(newStatus),
                description: description || `Status atualizado para ${getStatusLabel(newStatus)}`,
                updatedBy: responsible
            };

            const updateData = {
                status: newStatus,
                trackingHistory: [...existingHistory, newHistoryEntry],
                updatedAt: new Date()
            };

            const result = await updateDocument(orderId, updateData);

            if (result.success) {
                addToast(`Status do pedido atualizado para ${getStatusLabel(newStatus)}`, 'Success');
                await refreshOrders();

                // ✅ NOTIFICAR CRIADOR SOBRE APROVAÇÃO/REJEIÇÃO
                if (newStatus === ORDER_STATUS.APPROVED || newStatus === ORDER_STATUS.REJECTED) {
                    await notifyOrderCreator(orderId, newStatus, order?.code || '');
                }
            } else {
                addToast(`Erro ao atualizar status: ${result.error}`, 'Error');
            }
        } catch (error) {
            addToast(`Erro ao atualizar status: ${error.message}`, 'Error');
        } finally {
            setFormLoading(false);
        }
    };

    const handleConfirmOrder = async (orderId) => {
        if (!orderId) {
            addToast('ID do pedido não encontrado', 'Error');
            return;
        }

        setConfirmOrderLoading(true);
        try {
            // Primeiro, buscar o pedido para obter os dados
            const orderDoc = await getDoc(doc(db, 'sales-orders', orderId));
            if (!orderDoc.exists()) {
                addToast('Pedido não encontrado', 'Error');
                return;
            }
            const orderData = orderDoc.data();

            const result = await updateDocument(orderId, {
                status: ORDER_STATUS.PENDING,
                confirmedAt: new Date(),
                confirmedBy: responsible,
                updatedAt: new Date()
            });

            if (result.success) {
                addToast('Pedido confirmado com sucesso! Agora aguardando aprovação.', 'Success');

                // ✅ NOTIFICAR APROVADORES
                await notifyApprovers({
                    ...orderData,
                    code: orderData.code,
                    clientName: orderData.clientName,
                    calculatedValues: orderData.calculatedValues,
                    items: orderData.items,
                    responsible: orderData.responsible
                }, orderId);

                setIsConfirmModalOpen(false);
                setSelectedOrder(null);
                await refreshOrders();
            } else {
                addToast(`Erro ao confirmar pedido: ${result.error}`, 'Error');
            }
        } catch (error) {
            addToast(`Erro ao confirmar pedido: ${error.message}`, 'Error');
        } finally {
            setConfirmOrderLoading(false);
        }
    };

    // ============================================
    // 2.8.5 HANDLERS WITH BOX COMPLETION WARNINGS
    // ============================================

    // Adicionar produto ao pedido - WITH BOX TYPE SUPPORT AND WARNING CHECK
    const handleAddProduct = useCallback(() => {
        if (!selectedProductId) return;

        const selectedOption = productOptions.find(opt => opt.value === selectedProductId);
        if (!selectedOption || !selectedOption.product) return;

        const product = selectedOption.product;
        const suggestedPrice = product.suggestedPrice ||
            (product.totalFormulationCost * (product.markup || 1)) ||
            0;

        const ncmConfig = ncmMap.get(product.ncmCode);

        if (ncmConfig) {
            applyNcmTaxConfig(ncmConfig);
            addToast(`Configuração fiscal do NCM ${product.ncmCode} carregada automaticamente`, 'Info');
        } else if (product.ncmCode) {
            addToast(`NCM ${product.ncmCode} não possui configuração fiscal cadastrada`, 'Warning');
        }

        // Get available box types for this product
        const availableBoxTypes = selectedOption.availableBoxTypes || [];

        // Get default box info
        const defaultBox = availableBoxTypes.length > 0 ? availableBoxTypes[0] : null;
        const defaultUnitsPerBox = defaultBox?.unitsPerBox || 0;
        const defaultQuantity = isBonusOrder ? '0' : '1';

        // Create the new item with box type info
        const newItem = {
            id: crypto.randomUUID(),
            productId: product.id,
            code: product.code || '',
            name: product.commercialName || product.name || '',
            quantity: defaultQuantity,
            unitPrice: isBonusOrder ? '0' : suggestedPrice.toString(),
            total: isBonusOrder ? '0' : suggestedPrice.toString(),
            isBonus: isBonusOrder,
            isSample: isSampleOrder,
            ncmCode: product.ncmCode || null,
            // Box type information
            availableBoxTypes: availableBoxTypes,
            selectedBoxTypeId: defaultBox?.boxTypeId || null,
            unitsPerBox: defaultUnitsPerBox,
            boxNetWeight: defaultBox?.boxNetWeight || 0,
            boxGrossWeight: defaultBox?.boxGrossWeight || 0,
            boxVolume: defaultBox?.boxVolume || 0
        };

        // Check box completion for the new item
        if (defaultUnitsPerBox > 0 && parseFloat(defaultQuantity) > 0) {
            const status = getBoxCompletionStatus(parseFloat(defaultQuantity), defaultUnitsPerBox);
            if (status && !status.isComplete) {
                setBoxCompletionWarnings(prev => ({
                    ...prev,
                    [newItem.id]: status
                }));
            }
        }

        setOrderItems(prev => [...prev, newItem]);
        setSelectedProductId('');
        setIsBonusOrder(false);
        setIsSampleOrder(false);
    }, [selectedProductId, productOptions, ncmMap, applyNcmTaxConfig, isBonusOrder, isSampleOrder, getBoxCompletionStatus]);

    // Remover item do pedido
    const handleRemoveItem = useCallback((itemId) => {
        // Remove warnings when item is removed
        setBoxCompletionWarnings(prev => {
            const newWarnings = { ...prev };
            delete newWarnings[itemId];
            return newWarnings;
        });
        setOrderItems(prev => prev.filter(item => item.id !== itemId));
    }, []);

    // Atualizar campo de um item - WITH BOX COMPLETION CHECK
    const handleUpdateItem = useCallback((itemId, field, value) => {
        setOrderItems(prev => prev.map(item => {
            if (item.id === itemId) {
                const updated = { ...item, [field]: value };

                // If changing the box type, update related fields
                if (field === 'selectedBoxTypeId') {
                    const selectedBox = item.availableBoxTypes?.find(b => b.boxTypeId === value);
                    if (selectedBox) {
                        updated.unitsPerBox = selectedBox.unitsPerBox || 0;
                        updated.boxNetWeight = selectedBox.boxNetWeight || 0;
                        updated.boxGrossWeight = selectedBox.boxGrossWeight || 0;
                        updated.boxVolume = selectedBox.boxVolume || 0;
                    }
                }

                if (field === 'quantity' || field === 'unitPrice') {
                    const qty = parseFloat(field === 'quantity' ? value : item.quantity) || 0;
                    const price = parseFloat(field === 'unitPrice' ? value : item.unitPrice) || 0;
                    updated.total = (qty * price).toFixed(2);
                }

                // Check box completion status
                const unitsPerBox = parseFloat(updated.unitsPerBox) || 0;
                const quantity = parseFloat(updated.quantity) || 0;

                if (unitsPerBox > 0 && quantity > 0) {
                    const status = getBoxCompletionStatus(quantity, unitsPerBox);
                    if (status && !status.isComplete) {
                        // Add warning state
                        setBoxCompletionWarnings(prevWarnings => ({
                            ...prevWarnings,
                            [itemId]: status
                        }));
                    } else {
                        // Remove warning if complete or no longer applicable
                        setBoxCompletionWarnings(prevWarnings => {
                            const newWarnings = { ...prevWarnings };
                            delete newWarnings[itemId];
                            return newWarnings;
                        });
                    }
                } else {
                    // Remove warning if no box type selected or quantity is 0
                    setBoxCompletionWarnings(prevWarnings => {
                        const newWarnings = { ...prevWarnings };
                        delete newWarnings[itemId];
                        return newWarnings;
                    });
                }

                return updated;
            }
            return item;
        }));
    }, [getBoxCompletionStatus]);

    const handleNewClick = () => {
        resetForm();
        setIsModalOpen(true);
    };

    const handleViewClick = (order) => {
        setSelectedOrder(order);
        setIsViewModalOpen(true);
    };

    const handleConfirmClick = (order) => {
        setSelectedOrder(order);
        setIsConfirmModalOpen(true);
    };

    const handleTrackingClick = (order) => {
        setSelectedOrder(order);
        setIsTrackingModalOpen(true);
    };

    // ============================================
    // 2.8.6 HANDLE EDIT - UPDATED FOR RETURNED_FOR_ANALYSIS
    // ============================================
    const handleEditClick = async (order) => {
        // Allow editing for DRAFT and RETURNED_FOR_ANALYSIS status
        if (order.status !== ORDER_STATUS.DRAFT && order.status !== ORDER_STATUS.RETURNED_FOR_ANALYSIS) {
            addToast('Apenas pedidos em status "Rascunho" ou "Retornado para Análise" podem ser editados', 'Warning');
            return;
        }

        if (!order || !order.id) {
            addToast('Erro ao carregar pedido para edição', 'Error');
            return;
        }

        // Ensure items have availableBoxTypes and box type selection info
        const itemsWithBoxInfo = (order.items || []).map(item => {
            // If item already has availableBoxTypes, use them
            if (item.availableBoxTypes && item.availableBoxTypes.length > 0) {
                return item;
            }

            // Try to get box types from product
            const product = products?.find(p => p.id === item.productId);
            if (product) {
                const technical = product.technical || {};
                const boxTypeConfigs = technical.boxTypeConfigs || [];
                const boxWeightsMap = technical.boxWeights || {};
                let availableBoxTypes = [];

                if (boxTypeConfigs.length > 0) {
                    availableBoxTypes = boxTypeConfigs.map(config => {
                        const box = boxTypesCache.find(b => b.id === config.boxTypeId);
                        return {
                            boxTypeId: config.boxTypeId,
                            unitsPerBox: config.unitsPerBox || 1,
                            boxName: box?.name || config.boxTypeId,
                            boxDetails: box || null,
                            boxNetWeight: boxWeightsMap[config.boxTypeId]?.boxNetWeight || 0,
                            boxGrossWeight: boxWeightsMap[config.boxTypeId]?.boxGrossWeight || 0,
                            boxVolume: boxWeightsMap[config.boxTypeId]?.boxVolume || 0
                        };
                    });
                } else if (technical.boxTypeId) {
                    const box = boxTypesCache.find(b => b.id === technical.boxTypeId);
                    availableBoxTypes = [{
                        boxTypeId: technical.boxTypeId,
                        unitsPerBox: technical.unitsPerBox || 1,
                        boxName: box?.name || technical.boxTypeId,
                        boxDetails: box || null,
                        boxNetWeight: technical.boxNetWeight || 0,
                        boxGrossWeight: technical.boxGrossWeight || 0,
                        boxVolume: 0
                    }];
                }

                return {
                    ...item,
                    availableBoxTypes: availableBoxTypes,
                    selectedBoxTypeId: item.selectedBoxTypeId || (availableBoxTypes.length > 0 ? availableBoxTypes[0].boxTypeId : null),
                    unitsPerBox: item.unitsPerBox || (availableBoxTypes.length > 0 ? availableBoxTypes[0].unitsPerBox : 0)
                };
            }

            return item;
        });

        setEditingItem(order);
        setSelectedClientId(order.clientId || '');
        setSelectedAccountExecutiveId(order.accountExecutiveId || '');
        setBillingAddress(order.billingAddress || '');
        setDeliveryAddress(order.deliveryAddress || '');
        setSelectedContactId(order.contactId || '');
        setContactPhone(order.contactPhone || '');
        setClientOrderNumber(order.clientOrderNumber || '');
        setClientDiscountPercent(order.clientDiscountPercent?.toString() || '');
        setDiscountTicket(order.discountTicket || { type: 'amount', value: '' });
        setDestination(order.destination || '');
        setShippingMethod(order.shippingMethod || 'sea');
        setSelectedPalletTypeId(order.palletTypeId || '');
        setOrderItems(itemsWithBoxInfo);
        setEstimatedIcms(order.estimatedIcms?.toString() || '');
        setEstimatedIcmsSt(order.estimatedIcmsSt?.toString() || '');
        setEstimatedFcpSt(order.estimatedFcpSt?.toString() || '');
        setEstimatedIpi(order.estimatedIpi?.toString() || '');
        setCommissionPercent(order.commissionPercent?.toString() || '');
        setCommissionValue(order.commissionValue?.toString() || '');
        setCommissionType(order.commissionType || 'percent');
        setCommercialNotes(order.commercialNotes || '');
        setWithoutSeals(order.withoutSeals || false);
        setSelectedPaymentMethodId(order.paymentMethodId || '');
        setSelectedBillingConditionId(order.billingConditionId || '');
        setDeliveryDate(order.deliveryDate || '');
        setDeliveryDateDisplay(formatToBrazilianDate(order.deliveryDate || ''));
        setProductionNotes(order.productionNotes || '');
        setUseImporter(order.useImporter || false);
        setSelectedImporterId(order.selectedImporterId || '');
        setImporterCompanyName(order.importerCompanyName || '');
        setImporterAddress(order.importerAddress || '');
        setImporterContact(order.importerContact || '');
        setImporterPhone(order.importerPhone || '');

        // Clear warnings when loading
        setBoxCompletionWarnings({});

        // Check box completion for loaded items
        itemsWithBoxInfo.forEach(item => {
            const unitsPerBox = parseFloat(item.unitsPerBox) || 0;
            const quantity = parseFloat(item.quantity) || 0;

            if (unitsPerBox > 0 && quantity > 0) {
                const status = getBoxCompletionStatus(quantity, unitsPerBox);
                if (status && !status.isComplete) {
                    setBoxCompletionWarnings(prev => ({
                        ...prev,
                        [item.id]: status
                    }));
                }
            }
        });

        setIsModalOpen(true);
    };

    // ============================================
    // 2.8.7 HANDLE DELETE - UPDATED FOR RETURNED_FOR_ANALYSIS
    // ============================================
    const handleDelete = (order) => {
        // Allow deletion for DRAFT and RETURNED_FOR_ANALYSIS status
        if (order.status !== ORDER_STATUS.DRAFT && order.status !== ORDER_STATUS.RETURNED_FOR_ANALYSIS) {
            addToast('Apenas pedidos em status "Rascunho" ou "Retornado para Análise" podem ser excluídos', 'Warning');
            return;
        }

        setDeleteModal({
            isOpen: true,
            order: order,
            title: 'Confirmar Exclusão',
            message: `Tem certeza que deseja excluir o pedido ${order.code}? Esta ação não pode ser desfeita.`,
            itemName: order.code,
            itemCode: order.clientName,
            confirmText: 'Excluir Pedido',
            cancelText: 'Cancelar',
            onConfirm: async () => {
                setDeleteModal(prev => ({ ...prev, isLoading: true }));
                try {
                    const result = await deleteDocument(order.id);
                    if (result.success) {
                        addToast('Pedido excluído com sucesso!', 'Success');
                        await refreshOrders();
                    } else {
                        addToast(`Erro ao excluir: ${result.error}`, 'Error');
                    }
                } catch (error) {
                    addToast('Ocorreu um erro inesperado.', 'Error');
                } finally {
                    setDeleteModal(null);
                }
            },
            onCancel: () => setDeleteModal(null),
            isLoading: false
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (formLoading) return;
        setFormStatus({ message: '', type: '' });

        if (!selectedClientId) {
            setFormStatus({ message: 'Selecione um cliente.', type: 'error' });
            return;
        }

        if (orderItems.length === 0) {
            setFormStatus({ message: 'Adicione pelo menos um produto ao pedido.', type: 'error' });
            return;
        }

        // Check for critical box completion warnings (items with >75% of a box missing)
        const criticalWarnings = Object.entries(boxCompletionWarnings)
            .filter(([_, warning]) => warning.remainingPercentage >= 75);

        if (criticalWarnings.length > 0) {
            const warningMessages = criticalWarnings.map(([itemId, warning]) => {
                const item = orderItems.find(i => i.id === itemId);
                const itemName = item?.name || item?.code || 'Item';
                return `• ${itemName}: ${warning.remainingUnits.toFixed(2)} un. restantes (${warning.remainingPercentage.toFixed(0)}% da caixa)\n  💡 Recomendado: ${warning.recommendedQuantity} un. (${warning.boxesToComplete} caixas completas)`;
            }).join('\n\n');

            if (!window.confirm(
                `⚠️ ATENÇÃO: Os seguintes produtos têm quantidades que não preenchem caixas completas:\n\n${warningMessages}\n\n` +
                `Isso pode resultar em caixas com espaço vazio e aumentar custos de frete.\n\n` +
                `Deseja continuar mesmo assim?`
            )) {
                return;
            }
        }

        setFormLoading(true);

        try {
            const orderCode = editingItem?.code || await generateOrderCode();
            const values = calculatedValues;
            const client = selectedClient || {};

            const orderData = {
                code: orderCode,
                clientId: selectedClientId,
                clientName: client.razaoSocial || '',
                clientEmail: client.email || client.emailUsuario || '',
                accountExecutiveId: selectedAccountExecutiveId || '',
                accountExecutiveName: executiveOptions.find(e => e.value === selectedAccountExecutiveId)?.label || '',
                billingAddress: billingAddress || '',
                deliveryAddress: deliveryAddress || '',
                contactId: selectedContactId || '',
                contactName: clientContacts.find(c => c.id === selectedContactId)?.name || '',
                contactPhone: contactPhone || '',
                clientOrderNumber: clientOrderNumber || '',
                shippingMethod: shippingMethod || 'sea',
                // NEW: Save pallet type selection
                palletTypeId: selectedPalletTypeId || null,
                clientDiscountPercent: parseFloat(clientDiscountPercent) || 0,
                discountTicket: discountTicket || { type: 'amount', value: '' },
                destination: destination || '',
                items: orderItems.map(item => ({
                    ...item,
                    quantity: parseFloat(item.quantity) || 0,
                    unitPrice: parseFloat(item.unitPrice) || 0,
                    total: parseFloat(item.total) || 0,
                    code: item.code || '',
                    name: item.name || '',
                    isBonus: item.isBonus || false,
                    isSample: item.isSample || false,
                    // Save box type info
                    selectedBoxTypeId: item.selectedBoxTypeId || null,
                    unitsPerBox: parseFloat(item.unitsPerBox) || 0,
                    boxNetWeight: parseFloat(item.boxNetWeight) || 0,
                    boxGrossWeight: parseFloat(item.boxGrossWeight) || 0,
                    boxVolume: parseFloat(item.boxVolume) || 0,
                    availableBoxTypes: item.availableBoxTypes || []
                })),
                estimatedIcms: parseFloat(estimatedIcms) || 0,
                estimatedIcmsSt: parseFloat(estimatedIcmsSt) || 0,
                estimatedFcpSt: parseFloat(estimatedFcpSt) || 0,
                estimatedIpi: parseFloat(estimatedIpi) || 0,
                commissionPercent: parseFloat(commissionPercent) || 0,
                commissionValue: parseFloat(commissionValue) || 0,
                commissionType: commissionType || 'percent',
                commercialNotes: commercialNotes || '',
                withoutSeals: withoutSeals || false,
                paymentMethodId: selectedPaymentMethodId || '',
                paymentMethodName: paymentMethodOptions.find(p => p.value === selectedPaymentMethodId)?.label || '',
                billingConditionId: selectedBillingConditionId || '',
                billingConditionName: billingConditionOptions.find(b => b.value === selectedBillingConditionId)?.label || '',
                deliveryDate: deliveryDate || '',
                productionNotes: productionNotes || '',
                responsible: responsible || 'Usuário',
                useImporter: useImporter || false,
                selectedImporterId: selectedImporterId || '',
                importerCompanyName: importerCompanyName || '',
                importerAddress: importerAddress || '',
                importerContact: importerContact || '',
                importerPhone: importerPhone || '',
                calculatedValues: {
                    itemsTotal: parseFloat(values.itemsTotal) || 0,
                    bonusTotal: parseFloat(values.bonusTotal) || 0,
                    discountAmount: parseFloat(values.discountAmount) || 0,
                    ticketDiscountAmount: parseFloat(values.ticketDiscountAmount) || 0,
                    subtotalWithDiscounts: parseFloat(values.subtotalWithDiscounts) || 0,
                    icms: parseFloat(values.icms) || 0,
                    icmsSt: parseFloat(values.icmsSt) || 0,
                    fcpSt: parseFloat(values.fcpSt) || 0,
                    ipi: parseFloat(values.ipi) || 0,
                    commissionTotal: parseFloat(values.commissionTotal) || 0,
                    total: parseFloat(values.total) || 0
                },
                // Add USD-specific field for clarity (same values)
                usdValues: {
                    itemsTotal: parseFloat(values.itemsTotal) || 0,
                    bonusTotal: parseFloat(values.bonusTotal) || 0,
                    discountAmount: parseFloat(values.discountAmount) || 0,
                    ticketDiscountAmount: parseFloat(values.ticketDiscountAmount) || 0,
                    subtotalWithDiscounts: parseFloat(values.subtotalWithDiscounts) || 0,
                    icms: parseFloat(values.icms) || 0,
                    icmsSt: parseFloat(values.icmsSt) || 0,
                    fcpSt: parseFloat(values.fcpSt) || 0,
                    ipi: parseFloat(values.ipi) || 0,
                    commissionTotal: parseFloat(values.commissionTotal) || 0,
                    total: parseFloat(values.total) || 0
                },
                // Currency indicator
                currency: 'USD',
                updatedAt: new Date()
            };

            let result;

            if (editingItem && editingItem.id) {
                // Preserve existing status if it's RETURNED_FOR_ANALYSIS, otherwise use DRAFT
                const currentStatus = editingItem.status;
                // If the order was returned for analysis, keep it as draft after editing
                // so it can be resubmitted
                if (currentStatus === ORDER_STATUS.RETURNED_FOR_ANALYSIS) {
                    orderData.status = ORDER_STATUS.DRAFT;
                    // Add tracking history entry for the edit
                    const existingHistory = editingItem.trackingHistory || [];
                    const newHistoryEntry = {
                        date: new Date().toLocaleDateString('pt-BR'),
                        status: 'Editado após retorno',
                        description: `Pedido editado após retorno para análise por ${responsible}`,
                        updatedBy: responsible
                    };
                    orderData.trackingHistory = [...existingHistory, newHistoryEntry];
                } else {
                    orderData.status = currentStatus || ORDER_STATUS.DRAFT;
                }
                result = await updateDocument(editingItem.id, orderData);
                if (result.success) {
                    addToast('Pedido de venda atualizado com sucesso!', 'Success');
                    setTimeout(() => resetForm(), 1500);
                } else {
                    setFormStatus({ message: `Erro: ${result.error}`, type: 'error' });
                }
            } else {
                orderData.createdAt = new Date();
                orderData.status = ORDER_STATUS.DRAFT;
                result = await addDocument(orderData);
                if (result.success) {
                    addToast('Pedido de venda criado com sucesso!', 'Success');
                    setTimeout(() => resetForm(), 1500);
                } else {
                    setFormStatus({ message: `Erro: ${result.error}`, type: 'error' });
                }
            }

            if (result?.success && refreshOrders) {
                await refreshOrders();
            }
        } catch (error) {
            console.error('Error in handleSubmit:', error);
            setFormStatus({ message: `Erro ao salvar: ${error.message}`, type: 'error' });
        } finally {
            setFormLoading(false);
        }
    };

    // ============================================
    // 2.9 FILTROS E PAGINAÇÃO
    // ============================================

    const filteredOrders = useMemo(() => {
        if (!orders || !Array.isArray(orders)) return [];

        const lowerSearch = searchTerm.toLowerCase().trim();

        return orders.filter(order => {
            if (!order) return false;
            const code = order.code || '';
            const clientName = order.clientName || '';

            const matchesSearch = code.toLowerCase().includes(lowerSearch) ||
                clientName.toLowerCase().includes(lowerSearch);

            let matchesStatus = true;
            if (statusFilter === 'draft') {
                matchesStatus = order.status === ORDER_STATUS.DRAFT;
            } else if (statusFilter === 'pending') {
                matchesStatus = order.status === ORDER_STATUS.PENDING;
            } else if (statusFilter === 'approved') {
                matchesStatus = order.status === ORDER_STATUS.APPROVED ||
                    order.status === ORDER_STATUS.IN_PRODUCTION ||
                    order.status === ORDER_STATUS.SHIPPED ||
                    order.status === ORDER_STATUS.DELIVERED;
            } else if (statusFilter === 'cancelled') {
                matchesStatus = order.status === ORDER_STATUS.CANCELLED;
            } else if (statusFilter === 'returned') {
                matchesStatus = order.status === ORDER_STATUS.RETURNED_FOR_ANALYSIS;
            }

            return matchesSearch && matchesStatus;
        }).sort((a, b) => {
            const dateA = a.createdAt?.toDate ? a.createdAt.toDate() : (a.createdAt ? new Date(a.createdAt) : new Date(0));
            const dateB = b.createdAt?.toDate ? b.createdAt.toDate() : (b.createdAt ? new Date(b.createdAt) : new Date(0));
            return dateB - dateA;
        });
    }, [orders, searchTerm, statusFilter]);

    const totalPages = Math.ceil(filteredOrders.length / ITEMS_PER_PAGE);
    const currentOrders = useMemo(() => {
        const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
        return filteredOrders.slice(startIndex, startIndex + ITEMS_PER_PAGE);
    }, [filteredOrders, currentPage]);

    const handlePageChange = (newPage) => {
        if (newPage >= 1 && newPage <= totalPages) setCurrentPage(newPage);
    };

    const showSkeleton = ordersLoading && orders?.length === 0;

    // ============================================
    // 2.10 PRINT FUNCTION
    // ============================================
    const printOrder = (order) => {
        if (!order) return;

        const printWindow = window.open('', '_blank');

        const formatCurrencyPrint = (value) => {
            if (!value && value !== 0) return '$ 0.00';
            return `$ ${Number(value).toLocaleString('en-US', { minimumFractionDigits: 2 })}`;
        };

        const formatDatePrint = (dateValue) => {
            if (!dateValue) return '-';
            if (dateValue.toDate) return dateValue.toDate().toLocaleDateString('pt-BR');
            return new Date(dateValue).toLocaleDateString('pt-BR');
        };

        const getStatusLabelPrint = (status) => {
            const statusConfig = ORDER_STATUS_LABELS[status] || ORDER_STATUS_LABELS[ORDER_STATUS.DRAFT];
            return statusConfig.label;
        };

        const values = order.calculatedValues || {};

        const printContent = `
            <!DOCTYPE html>
            <html>
            <head>
                <title>Pedido de Venda - ${order.code}</title>
                <meta charset="UTF-8">
                <style>
                    body { font-family: Arial, sans-serif; margin: 20px; padding: 20px; }
                    .header { text-align: center; margin-bottom: 30px; border-bottom: 2px solid #333; padding-bottom: 10px; }
                    .title { font-size: 24px; font-weight: bold; margin: 0; }
                    .subtitle { color: #666; margin: 5px 0; }
                    .section { margin-bottom: 20px; page-break-inside: avoid; }
                    .section-title { background: #f0f0f0; padding: 8px; font-weight: bold; margin-bottom: 10px; }
                    .info-grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 10px; margin-bottom: 15px; }
                    .info-item { margin-bottom: 5px; }
                    .info-label { font-weight: bold; font-size: 12px; color: #666; }
                    .info-value { font-size: 14px; }
                    table { width: 100%; border-collapse: collapse; margin-bottom: 15px; }
                    th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
                    th { background: #f0f0f0; font-weight: bold; }
                    .text-right { text-align: right; }
                    .total-row { font-weight: bold; background: #f9f9f9; }
                    .footer { margin-top: 30px; text-align: center; font-size: 12px; color: #666; border-top: 1px solid #ddd; padding-top: 10px; }
                    @media print { body { margin: 0; padding: 10px; } .no-print { display: none; } }
                </style>
            </head>
            <body>
                <div class="header">
                    <div class="title">PEDIDO DE VENDA</div>
                    <div class="subtitle">Nº ${order.code}</div>
                    <div class="subtitle">Data: ${formatDatePrint(order.createdAt)}</div>
                </div>

                <div class="section">
                    <div class="section-title">INFORMAÇÕES DO CLIENTE</div>
                    <div class="info-grid">
                        <div class="info-item"><div class="info-label">Cliente:</div><div class="info-value">${order.clientName || '-'}</div></div>
                        <div class="info-item"><div class="info-label">Status:</div><div class="info-value">${getStatusLabelPrint(order.status)}</div></div>
                        <div class="info-item"><div class="info-label">Endereço de Entrega:</div><div class="info-value">${order.deliveryAddress || '-'}</div></div>
                        <div class="info-item"><div class="info-label">Contato:</div><div class="info-value">${order.contactName || '-'} ${order.contactPhone ? `(${order.contactPhone})` : ''}</div></div>
                        <div class="info-item"><div class="info-label">O.C. do Cliente:</div><div class="info-value">${order.clientOrderNumber || '-'}</div></div>
                        <div class="info-item"><div class="info-label">Executivo de Contas:</div><div class="info-value">${order.accountExecutiveName || '-'}</div></div>
                        <div class="info-item"><div class="info-label">Método de Envio:</div><div class="info-value">${order.shippingMethod === 'air' ? '✈️ Aéreo' : '🚢 Marítimo'}</div></div>
                        ${order.palletTypeId ? `<div class="info-item"><div class="info-label">Tipo de Palete:</div><div class="info-value">${allPalletTypes.find(p => p.id === order.palletTypeId)?.name || order.palletTypeId}</div></div>` : ''}
                    </div>
                </div>

                ${order.useImporter ? `
                <div class="section">
                    <div class="section-title">INFORMAÇÕES DO IMPORTADOR</div>
                    <div class="info-grid">
                        <div class="info-item"><div class="info-label">Empresa Importadora:</div><div class="info-value">${order.importerCompanyName || '-'}</div></div>
                        <div class="info-item"><div class="info-label">Endereço do Importador:</div><div class="info-value">${order.importerAddress || '-'}</div></div>
                        <div class="info-item"><div class="info-label">Contato do Importador:</div><div class="info-value">${order.importerContact || '-'} ${order.importerPhone ? `(${order.importerPhone})` : ''}</div></div>
                    </div>
                </div>
                ` : ''}

                <div class="section">
                    <div class="section-title">PRODUTOS</div>
                    <table>
                        <thead><tr><th>#</th><th>Código</th><th>Produto</th><th class="text-right">Qtde</th><th class="text-right">Preço Unit.</th><th class="text-right">Total</th><th>Caixa</th></tr></thead>
                        <tbody>
                            ${order.items?.map((item, idx) => {
            const boxName = item.availableBoxTypes?.find(b => b.boxTypeId === item.selectedBoxTypeId)?.boxName ||
                item.selectedBoxTypeId ||
                (item.availableBoxTypes?.length > 0 ? item.availableBoxTypes[0].boxName : '');
            return `
                                <tr>
                                    <td>${idx + 1}</td>
                                    <td>${item.code || '-'}</td>
                                    <td>${item.name || '-'}${item.isBonus ? ' (BÔNUS)' : ''}</td>
                                    <td class="text-right">${item.quantity || 0}</td>
                                    <td class="text-right">${formatCurrencyPrint(item.unitPrice)}</td>
                                    <td class="text-right">${formatCurrencyPrint(item.total)}</td>
                                    <td>${boxName || '-'}</td>
                                </tr>
                                `;
        }).join('') || '<tr><td colspan="7" class="text-right">Nenhum produto</td></tr>'}
                        </tbody>
                    </table>
                </div>

                <div class="section">
                    <div class="section-title">RESUMO DE VALORES</div>
                    <table style="width: 300px; float: right;">
                        <tr><td>Subtotal:</td><td class="text-right">${formatCurrencyPrint(values.itemsTotal)}</td></tr>
                        ${values.bonusTotal > 0 ? `<tr><td>Bonificação:</td><td class="text-right">${formatCurrencyPrint(values.bonusTotal)}</td></tr>` : ''}
                        ${values.discountAmount > 0 ? `<tr><td>Desconto Cliente:</td><td class="text-right">- ${formatCurrencyPrint(values.discountAmount)}</td></tr>` : ''}
                        ${values.ticketDiscountAmount > 0 ? `<tr><td>Desconto Boleto:</td><td class="text-right">- ${formatCurrencyPrint(values.ticketDiscountAmount)}</td></tr>` : ''}
                        <tr class="total-row"><td><strong>TOTAL</strong></td><td class="text-right"><strong>${formatCurrencyPrint(values.total)}</strong></td></tr>
                    </table>
                    <div style="clear: both;"></div>
                </div>

                ${order.productionNotes ? `<div class="section"><div class="section-title">OBSERVAÇÕES PARA PRODUÇÃO</div><div class="info-value">${order.productionNotes}</div></div>` : ''}

                <div class="footer">Documento gerado eletronicamente em ${new Date().toLocaleString('pt-BR')}</div>
                <div class="no-print" style="text-align: center; margin-top: 20px;"><button onclick="window.print();" style="padding: 10px 20px;">Imprimir</button> <button onclick="window.close();" style="padding: 10px 20px; margin-left: 10px;">Fechar</button></div>
            </body>
            </html>
        `;

        printWindow.document.write(printContent);
        printWindow.document.close();
    };

    // ============================================
    // 2.11 RENDERIZAÇÃO DO MODAL
    // ============================================

    const renderOrderModal = () => {
        if (!isModalOpen) return null;

        // Count warnings for summary
        const warningCount = Object.keys(boxCompletionWarnings).length;
        const criticalCount = Object.values(boxCompletionWarnings)
            .filter(w => w.remainingPercentage >= 75).length;

        return (
            <div className={styles.modalBackdrop}>
                <div className={styles.formContainer} style={{ maxWidth: '1400px' }}>
                    <div className={styles.modalHeader}>
                        <div>
                            <h2>{editingItem && editingItem.id ? `Editar Pedido` : `Novo Pedido`}</h2>
                            {editingItem && editingItem.code && (
                                <p className={styles.modalSubtitle}>
                                    {editingItem.id ? 'Editando: ' : 'Novo: '}<strong>{editingItem.code}</strong>
                                    {editingItem.status === ORDER_STATUS.RETURNED_FOR_ANALYSIS && (
                                        <span style={{ marginLeft: '8px', color: '#f39c12', fontSize: '14px' }}>
                                            ⚠️ Retornado para Análise
                                        </span>
                                    )}
                                </p>
                            )}
                            {warningCount > 0 && (
                                <p className={styles.warningSummaryBadge}>
                                    ⚠️ {warningCount} produto(s) com caixa incompleta
                                    {criticalCount > 0 && ` (${criticalCount} crítico${criticalCount > 1 ? 's' : ''})`}
                                </p>
                            )}
                        </div>
                        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                            <button
                                type="button"
                                onClick={resetForm}
                                className={styles.modalCloseButton}
                                disabled={formLoading}
                            >
                                <FiX size={24} />
                            </button>
                        </div>
                    </div>

                    {formStatus.message && (
                        <div className={`${styles.alert} ${styles[formStatus.type]}`}>
                            <span className={styles.alertIcon}>
                                {formStatus.type === 'error' && <FiAlertCircle size={18} />}
                                {formStatus.type === 'success' && <FiCheck size={18} />}
                                {formStatus.type === 'info' && <FiInfo size={18} />}
                            </span>
                            {formStatus.message}
                        </div>
                    )}

                    <form onSubmit={handleSubmit} className={styles.formContent}>
                        {/* SEÇÃO 1: IDENTIFICAÇÃO DO CLIENTE */}
                        <div className={styles.formSection}>
                            <div className={styles.sectionHeader}>
                                <div className={styles.sectionIcon}><MdBusiness size={18} /></div>
                                <h3 className={styles.sectionTitle}>Identificação do Cliente</h3>
                                <span className={styles.sectionBadge}>Obrigatório</span>
                            </div>
                            <div className={styles.formGrid}>
                                <div className={styles.inputGroup} style={{ gridColumn: 'span 2' }}>
                                    <label>Cliente <span className={styles.required}>*</span></label>
                                    <ComboBox
                                        options={clientOptions}
                                        value={selectedClientId}
                                        onChange={setSelectedClientId}
                                        placeholder="Buscar cliente por código ou nome..."
                                        label=""
                                        required={true}
                                        disabled={formLoading || clientsLoading}
                                        loading={clientsLoading}
                                        noResultsMessage="Nenhum cliente encontrado"
                                        getOptionLabel={(option) => option.label || ''}
                                        getOptionValue={(option) => option.value || ''}
                                    />
                                </div>

                                <div className={styles.inputGroup}>
                                    <label>Executivo de Contas</label>
                                    <select
                                        value={selectedAccountExecutiveId}
                                        onChange={(e) => setSelectedAccountExecutiveId(e.target.value)}
                                        className={styles.select}
                                        disabled={formLoading || executivesLoading}
                                    >
                                        <option value="">Selecione...</option>
                                        {executiveOptions.map(exec => (
                                            <option key={exec.value} value={exec.value}>
                                                {exec.label}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                <div className={styles.inputGroup}>
                                    <label>Endereço Faturamento</label>
                                    <input
                                        type="text"
                                        value={billingAddress}
                                        onChange={(e) => setBillingAddress(e.target.value)}
                                        className={styles.input}
                                        disabled={formLoading}
                                        placeholder="Endereço de faturamento"
                                    />
                                </div>

                                <div className={styles.inputGroup}>
                                    <label>Endereço Entrega</label>
                                    <select
                                        value={deliveryAddress}
                                        onChange={(e) => setDeliveryAddress(e.target.value)}
                                        className={styles.select}
                                        disabled={formLoading || !selectedClient}
                                    >
                                        <option value="">Selecione um endereço de entrega...</option>
                                        {clientAddresses.map(addr => (
                                            <option key={addr.id} value={addr.formattedAddress}>
                                                {addr.tipo || 'Endereço'}: {addr.formattedAddress}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                <div className={styles.inputGroup}>
                                    <label>Contato</label>
                                    <select
                                        value={selectedContactId}
                                        onChange={(e) => handleContactChange(e.target.value)}
                                        className={styles.select}
                                        disabled={formLoading || !selectedClient}
                                    >
                                        <option value="">Selecione um contato...</option>
                                        {clientContacts.map(contact => (
                                            <option key={contact.id} value={contact.id}>
                                                {contact.name} - {contact.cargo || 'Sem cargo'} {contact.telefone && `(${contact.telefone})`}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                <div className={styles.inputGroup}>
                                    <label>Telefone de Contato</label>
                                    <input
                                        type="text"
                                        value={contactPhone}
                                        onChange={(e) => setContactPhone(e.target.value)}
                                        className={styles.input}
                                        disabled={formLoading}
                                        placeholder="(00) 00000-0000"
                                    />
                                </div>

                                <div className={styles.inputGroup}>
                                    <label>O.C. do Cliente</label>
                                    <input
                                        type="text"
                                        value={clientOrderNumber}
                                        onChange={(e) => setClientOrderNumber(e.target.value)}
                                        className={styles.input}
                                        disabled={formLoading}
                                        placeholder="Número do pedido do cliente"
                                    />
                                </div>

                                <div className={styles.inputGroup}>
                                    <label>Desconto Cliente (%)</label>
                                    <input
                                        type="number"
                                        step="0.01"
                                        value={clientDiscountPercent}
                                        onChange={(e) => setClientDiscountPercent(e.target.value)}
                                        className={styles.input}
                                        disabled={formLoading}
                                        placeholder="0,00"
                                    />
                                </div>

                                <div className={styles.inputGroup}>
                                    <label>Desconto do Boleto</label>
                                    <div style={{ display: 'flex', gap: '8px' }}>
                                        <select
                                            value={discountTicket.type}
                                            onChange={(e) => setDiscountTicket(prev => ({ ...prev, type: e.target.value }))}
                                            className={styles.select}
                                            style={{ width: '80px' }}
                                            disabled={formLoading}
                                        >
                                            <option value="amount">R$</option>
                                            <option value="percent">%</option>
                                        </select>
                                        <input
                                            type="number"
                                            step="0.01"
                                            value={discountTicket.value}
                                            onChange={(e) => setDiscountTicket(prev => ({ ...prev, value: e.target.value }))}
                                            className={styles.input}
                                            disabled={formLoading}
                                            placeholder="0,00"
                                            style={{ flex: 1 }}
                                        />
                                    </div>
                                </div>

                                <div className={styles.inputGroup}>
                                    <label>Destinação</label>
                                    <select
                                        value={destination}
                                        onChange={(e) => setDestination(e.target.value)}
                                        className={styles.select}
                                        disabled={formLoading}
                                    >
                                        <option value="">Selecione...</option>
                                        {DESTINATION_OPTIONS.map(opt => (
                                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                                        ))}
                                    </select>
                                </div>
                                <div className={styles.inputGroup}>
                                    <label htmlFor="shippingMethod">
                                        Método de Envio <span className={styles.required}>*</span>
                                    </label>
                                    <select
                                        id="shippingMethod"
                                        value={shippingMethod}
                                        onChange={(e) => setShippingMethod(e.target.value)}
                                        className={styles.select}
                                        required
                                    >
                                        <option value="sea">Marítimo (Sea)</option>
                                        <option value="air">Aéreo (Air)</option>
                                    </select>
                                    <small className={styles.fieldHint}>
                                        Define o cálculo de caixas por palete e altura na Packing List
                                    </small>
                                </div>
                            </div>
                        </div>

                        {/* SEÇÃO 1.5: SELEÇÃO DE PALETE */}
                        <div className={styles.formSection} style={{ borderLeft: '3px solid #8e44ad' }}>
                            <div className={styles.sectionHeader}>
                                <div className={styles.sectionIcon}><FiBox size={18} style={{ color: '#8e44ad' }} /></div>
                                <h3 className={styles.sectionTitle} style={{ color: '#8e44ad' }}>Configuração de Palete</h3>
                                <span className={styles.sectionBadge} style={{ backgroundColor: '#8e44ad', color: 'white' }}>Opcional</span>
                            </div>
                            <div className={styles.formGrid}>
                                <div className={styles.inputGroup} style={{ gridColumn: 'span 2' }}>
                                    <label htmlFor="palletType">Tipo de Palete</label>
                                    <select
                                        id="palletType"
                                        value={selectedPalletTypeId}
                                        onChange={(e) => setSelectedPalletTypeId(e.target.value)}
                                        className={styles.select}
                                        disabled={formLoading || loadingPalletTypes}
                                    >
                                        <option value="">Selecionar automaticamente</option>
                                        {allPalletTypes.map(pallet => {
                                            // Count how many box types this pallet supports
                                            const boxCount = pallet.boxConfigs?.length || 0;
                                            return (
                                                <option key={pallet.id} value={pallet.id}>
                                                    {pallet.name} ({pallet.palletWidth || 100}x{pallet.palletDepth || 120}cm, {boxCount} tipos de caixa)
                                                </option>
                                            );
                                        })}
                                    </select>
                                    <small className={styles.fieldHint}>
                                        Selecione o tipo de palete para este pedido. Deixe em branco para seleção automática baseada no tipo de caixa.
                                        {selectedPalletTypeId && (
                                            <span style={{ display: 'block', marginTop: '4px', color: '#8e44ad' }}>
                                                ✅ Palete selecionado: {allPalletTypes.find(p => p.id === selectedPalletTypeId)?.name || selectedPalletTypeId}
                                            </span>
                                        )}
                                    </small>
                                </div>
                            </div>
                        </div>

                        {/* NOVA SEÇÃO: INFORMAÇÕES DO IMPORTADOR */}
                        <div className={styles.formSection} style={{ borderLeft: '3px solid #ff6b6b' }}>
                            <div className={styles.sectionHeader}>
                                <div className={styles.sectionIcon}><FaShip size={18} style={{ color: '#ff6b6b' }} /></div>
                                <h3 className={styles.sectionTitle} style={{ color: '#ff6b6b' }}>Informações do Importador</h3>
                                <span className={styles.sectionBadge} style={{ backgroundColor: '#ff6b6b', color: 'white' }}>Opcional</span>
                            </div>
                            <div className={styles.formGrid}>
                                <div className={styles.inputGroup} style={{ gridColumn: 'span 2' }}>
                                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                        <input
                                            type="checkbox"
                                            checked={useImporter}
                                            onChange={(e) => {
                                                setUseImporter(e.target.checked);
                                                if (!e.target.checked) {
                                                    setImporterAddress('');
                                                    setImporterCompanyName('');
                                                    setImporterContact('');
                                                    setImporterPhone('');
                                                    setSelectedImporterId('');
                                                }
                                            }}
                                            disabled={formLoading || importerAddresses.length === 0}
                                        />
                                        Utilizar endereço de importador diferente do consignatário
                                    </label>
                                    {importerAddresses.length === 0 && selectedClient && (
                                        <small style={{ color: '#ff6b6b', display: 'block', marginTop: '4px' }}>
                                            ⚠️ Este cliente não possui endereços cadastrados como importador.
                                            Cadastre um endereço de importador no cadastro do cliente.
                                        </small>
                                    )}
                                </div>

                                {useImporter && (
                                    <>
                                        <div className={styles.inputGroup} style={{ gridColumn: 'span 2' }}>
                                            <label>Empresa Importadora</label>
                                            <input
                                                type="text"
                                                value={importerCompanyName}
                                                className={styles.input}
                                                disabled
                                                style={{ backgroundColor: '#f5f5f5', fontWeight: 'bold', color: '#2d3748' }}
                                                placeholder="A empresa importadora será carregada ao selecionar o endereço"
                                            />
                                            <small className={styles.fieldHint}>Nome da empresa importadora (do cadastro do cliente)</small>
                                        </div>

                                        <div className={styles.inputGroup} style={{ gridColumn: 'span 2' }}>
                                            <label>Endereço do Importador <span className={styles.required}>*</span></label>
                                            <select
                                                value={selectedImporterId}
                                                onChange={(e) => handleImporterChange(e.target.value)}
                                                className={styles.select}
                                                disabled={formLoading || importerAddresses.length === 0}
                                                required={useImporter}
                                            >
                                                <option value="">Selecione um endereço de importador...</option>
                                                {importerAddresses.map(addr => (
                                                    <option key={addr.id} value={addr.id}>
                                                        {addr.tipo || 'Importador'}: {addr.formattedAddress}
                                                        {addr.importerCompanyName && ` (${addr.importerCompanyName})`}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>

                                        <div className={styles.inputGroup}>
                                            <label>Contato do Importador</label>
                                            <select
                                                value={importerContact}
                                                onChange={(e) => handleImporterContactChange(e.target.value)}
                                                className={styles.select}
                                                disabled={formLoading || importerContacts.length === 0}
                                            >
                                                <option value="">Selecione um contato...</option>
                                                {importerContacts.map(contact => (
                                                    <option key={contact.id} value={contact.id}>
                                                        {contact.name} - {contact.cargo || 'Sem cargo'} {contact.telefone && `(${contact.telefone})`}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>

                                        <div className={styles.inputGroup}>
                                            <label>Telefone do Importador</label>
                                            <input
                                                type="text"
                                                value={importerPhone}
                                                onChange={(e) => setImporterPhone(e.target.value)}
                                                className={styles.input}
                                                disabled={formLoading}
                                                placeholder="(00) 00000-0000"
                                            />
                                        </div>
                                    </>
                                )}
                            </div>
                        </div>

                        {/* SEÇÃO 2: LISTA DE PRODUTOS - WITH BOX TYPE SELECTOR AND WARNINGS */}
                        <div className={styles.formSection}>
                            <div className={styles.sectionHeader}>
                                <div className={styles.sectionIcon}><FiPackage size={18} /></div>
                                <h3 className={styles.sectionTitle}>Lista de Produto(s) Selecionado(s)</h3>
                                <span className={styles.sectionBadge}>{orderItems.length} itens</span>
                                {Object.keys(boxCompletionWarnings).length > 0 && (
                                    <span className={styles.sectionBadge} style={{ backgroundColor: '#ffc107', color: '#333' }}>
                                        ⚠️ {Object.keys(boxCompletionWarnings).length} caixa(s) incompleta(s)
                                    </span>
                                )}
                            </div>
                            <div style={{ padding: '0px' }}>
                                <div className={styles.formGrid} style={{ marginBottom: '20px' }}>
                                    <div className={styles.inputGroup} style={{ gridColumn: 'span 2' }}>
                                        <label>Inserir Produto</label>
                                        <ComboBox
                                            options={productOptions}
                                            value={selectedProductId}
                                            onChange={setSelectedProductId}
                                            placeholder="Buscar produto por código ou nome..."
                                            label=""
                                            required={false}
                                            disabled={formLoading || productsLoading}
                                            loading={productsLoading}
                                            noResultsMessage="Nenhum produto encontrado"
                                            getOptionLabel={(option) => option.label || ''}
                                            getOptionValue={(option) => option.value || ''}
                                        />
                                    </div>

                                    <div className={styles.inputGroup}>
                                        <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                            <input
                                                type="checkbox"
                                                checked={isBonusOrder}
                                                onChange={(e) => setIsBonusOrder(e.target.checked)}
                                                disabled={formLoading}
                                            />
                                            Produto Bonificado
                                        </label>
                                    </div>

                                    <div className={styles.inputGroup}>
                                        <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                            <input
                                                type="checkbox"
                                                checked={isSampleOrder}
                                                onChange={(e) => setIsSampleOrder(e.target.checked)}
                                                disabled={formLoading}
                                            />
                                            Amostra Grátis
                                        </label>
                                    </div>

                                    <div className={styles.inputGroup} style={{ justifyContent: 'flex-end', display: 'flex', alignItems: 'flex-end', gap: '8px' }}>
                                        <button
                                            type="button"
                                            onClick={handleAddProduct}
                                            className={styles.addButton}
                                            disabled={formLoading || !selectedProductId}
                                        >
                                            <FiPlus size={18} /> Adicionar Produto
                                        </button>
                                    </div>
                                </div>

                                <div className={styles.tableContainer}>
                                    <table className={styles.table}>
                                        <thead>
                                            <tr>
                                                <th style={{ width: '50px' }}>#</th>
                                                <th>Código</th>
                                                <th>Nome do Produto</th>
                                                <th style={{ width: '100px' }}>Qtde</th>
                                                <th style={{ width: '120px' }}>Preço unit.</th>
                                                <th style={{ width: '120px' }}>Total</th>
                                                <th style={{ width: '200px' }}>Tipo de Caixa</th>
                                                <th style={{ width: '100px' }}>Ações</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {orderItems.length === 0 ? (
                                                <tr>
                                                    <td colSpan="8" className={styles.emptyTable}>
                                                        Nenhum produto adicionado ao pedido.
                                                    </td>
                                                </tr>
                                            ) : (
                                                orderItems.map((item, index) => {
                                                    const ncmConfig = ncmMap.get(item.ncmCode);
                                                    const quantity = parseFloat(item.quantity) || 0;
                                                    const unitPrice = parseFloat(item.unitPrice) || 0;
                                                    const total = quantity * unitPrice;
                                                    const unitsPerBox = item.unitsPerBox || 0;
                                                    const boxesNeeded = unitsPerBox > 0 && quantity > 0 ? quantity / unitsPerBox : quantity;
                                                    const hasBoxTypes = item.availableBoxTypes && item.availableBoxTypes.length > 0;

                                                    // Get box completion warning
                                                    const warning = boxCompletionWarnings[item.id];
                                                    const hasWarning = warning && !warning.isComplete && quantity > 0 && unitsPerBox > 0;
                                                    const severity = hasWarning ? getWarningSeverity(warning.remainingPercentage) : 'none';

                                                    // Determine row className
                                                    let rowClassName = item.isBonus ? styles.bonusRow : '';
                                                    if (hasWarning) {
                                                        rowClassName += ` ${styles.warningRow}`;
                                                    }

                                                    return (
                                                        <React.Fragment key={item.id}>
                                                            <tr className={rowClassName}>
                                                                <td style={{ textAlign: 'center' }}>{index + 1}</td>
                                                                <td>
                                                                    <input
                                                                        type="text"
                                                                        value={item.code || ''}
                                                                        onChange={(e) => handleUpdateItem(item.id, 'code', e.target.value)}
                                                                        className={styles.input}
                                                                        disabled={formLoading}
                                                                        placeholder="Código"
                                                                    />
                                                                </td>
                                                                <td>
                                                                    <input
                                                                        type="text"
                                                                        value={item.name || ''}
                                                                        onChange={(e) => handleUpdateItem(item.id, 'name', e.target.value)}
                                                                        className={styles.input}
                                                                        disabled={formLoading}
                                                                        placeholder="Nome do produto"
                                                                    />
                                                                </td>
                                                                <td style={{ width: '100px' }}>
                                                                    <input
                                                                        type="number"
                                                                        step="0.01"
                                                                        value={item.quantity || ''}
                                                                        onChange={(e) => handleUpdateItem(item.id, 'quantity', e.target.value)}
                                                                        className={`${styles.input} ${hasWarning ? styles.warningInput : ''}`}
                                                                        disabled={formLoading}
                                                                        placeholder="Qtde"
                                                                        style={hasWarning ? { borderColor: severity === 'critical' ? '#ff5722' : '#ffc107' } : {}}
                                                                    />
                                                                </td>
                                                                <td style={{ width: '120px' }}>
                                                                    <input
                                                                        type="number"
                                                                        step="0.01"
                                                                        value={item.unitPrice || ''}
                                                                        onChange={(e) => handleUpdateItem(item.id, 'unitPrice', e.target.value)}
                                                                        className={styles.input}
                                                                        disabled={formLoading || item.isBonus}
                                                                        placeholder="Preço unit."
                                                                    />
                                                                </td>
                                                                <td style={{ width: '120px' }}>
                                                                    <input
                                                                        type="text"
                                                                        value={`R$ ${total.toFixed(2)}`}
                                                                        className={styles.input}
                                                                        disabled
                                                                        style={{ backgroundColor: '#f5f5f5', fontWeight: 'bold' }}
                                                                    />
                                                                </td>
                                                                <td style={{ width: '200px' }}>
                                                                    {hasBoxTypes && item.availableBoxTypes.length > 1 ? (
                                                                        <select
                                                                            value={item.selectedBoxTypeId || ''}
                                                                            onChange={(e) => handleUpdateItem(item.id, 'selectedBoxTypeId', e.target.value)}
                                                                            className={styles.select}
                                                                            disabled={formLoading}
                                                                            style={{ fontSize: '12px', padding: '2px 4px', width: '100%' }}
                                                                        >
                                                                            {item.availableBoxTypes.map(box => (
                                                                                <option key={box.boxTypeId} value={box.boxTypeId}>
                                                                                    {box.boxName} ({box.unitsPerBox} un/box)
                                                                                </option>
                                                                            ))}
                                                                        </select>
                                                                    ) : hasBoxTypes && item.availableBoxTypes.length === 1 ? (
                                                                        <span style={{ fontSize: '12px', color: '#666' }}>
                                                                            {item.availableBoxTypes[0].boxName} ({item.availableBoxTypes[0].unitsPerBox} un/box)
                                                                        </span>
                                                                    ) : (
                                                                        <span style={{ fontSize: '12px', color: '#999', fontStyle: 'italic' }}>
                                                                            Sem caixa definida
                                                                        </span>
                                                                    )}
                                                                    {boxesNeeded > 0 && (
                                                                        <small style={{ display: 'block', fontSize: '10px', color: '#666' }}>
                                                                            {boxesNeeded.toFixed(2)} caixas necessárias
                                                                            {hasWarning && ` → ${warning.boxesToComplete} caixas completas`}
                                                                        </small>
                                                                    )}
                                                                    {/* Box completion warning indicator */}
                                                                    {hasWarning && (
                                                                        <div style={{
                                                                            display: 'flex',
                                                                            alignItems: 'center',
                                                                            marginTop: '4px',
                                                                            padding: '2px 6px',
                                                                            borderRadius: '3px',
                                                                            backgroundColor: severity === 'critical' ? '#ffe0e0' :
                                                                                severity === 'warning' ? '#fff3cd' :
                                                                                    severity === 'info' ? '#e3f2fd' : '#f5f5f5',
                                                                            fontSize: '10px',
                                                                            flexWrap: 'wrap',
                                                                            gap: '2px'
                                                                        }}>
                                                                            <span style={{ marginRight: '4px' }}>
                                                                                {severity === 'critical' ? '🚨' :
                                                                                    severity === 'warning' ? '⚠️' :
                                                                                        severity === 'info' ? 'ℹ️' : '📦'}
                                                                            </span>
                                                                            <span style={{
                                                                                color: severity === 'critical' ? '#d32f2f' :
                                                                                    severity === 'warning' ? '#856404' :
                                                                                        severity === 'info' ? '#0d47a1' : '#666',
                                                                                fontWeight: severity === 'critical' ? 'bold' : 'normal'
                                                                            }}>
                                                                                {warning.remainingUnits.toFixed(2)} un. restantes
                                                                                ({warning.remainingPercentage.toFixed(0)}% da caixa)
                                                                            </span>
                                                                            {severity === 'critical' && (
                                                                                <span style={{
                                                                                    marginLeft: '4px',
                                                                                    padding: '0 4px',
                                                                                    backgroundColor: '#d32f2f',
                                                                                    color: 'white',
                                                                                    borderRadius: '2px',
                                                                                    fontSize: '9px',
                                                                                    fontWeight: 'bold'
                                                                                }}>
                                                                                    +{warning.additionalUnitsNeeded} para completar
                                                                                </span>
                                                                            )}
                                                                            <span style={{
                                                                                marginLeft: '4px',
                                                                                padding: '0 4px',
                                                                                backgroundColor: '#4caf50',
                                                                                color: 'white',
                                                                                borderRadius: '2px',
                                                                                fontSize: '9px',
                                                                                fontWeight: 'bold'
                                                                            }}>
                                                                                → {warning.recommendedQuantity} un. ({warning.boxesToComplete} cx)
                                                                            </span>
                                                                        </div>
                                                                    )}
                                                                </td>
                                                                <td style={{ width: '100px', textAlign: 'center' }}>
                                                                    <button
                                                                        type="button"
                                                                        onClick={() => handleRemoveItem(item.id)}
                                                                        className={`${styles.actionButton} ${styles.deleteButton}`}
                                                                        disabled={formLoading}
                                                                        title="Remover item"
                                                                    >
                                                                        <FiTrash2 size={14} />
                                                                    </button>
                                                                </td>
                                                            </tr>

                                                            {/* Warning message row for critical cases */}
                                                            {hasWarning && severity === 'critical' && (
                                                                <tr className={styles.warningMessageRow}>
                                                                    <td colSpan="8" style={{
                                                                        padding: '4px 20px',
                                                                        backgroundColor: '#fff3e0',
                                                                        borderLeft: '4px solid #ff5722'
                                                                    }}>
                                                                        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', fontSize: '12px', color: '#bf360c', flexDirection: 'column' }}>
                                                                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                                                <span>🚨</span>
                                                                                <span>
                                                                                    <strong>Atenção:</strong> A quantidade de <strong>{quantity}</strong> unidades não preenche caixas completas.
                                                                                </span>
                                                                            </div>
                                                                            <div style={{ paddingLeft: '28px', display: 'flex', flexDirection: 'column', gap: '2px' }}>
                                                                                <span>
                                                                                    • {warning.completeBoxes} caixa(s) completa(s) + {warning.remainingUnits.toFixed(2)} unidades extras
                                                                                    ({warning.remainingPercentage.toFixed(0)}% da {warning.completeBoxes + 1}ª caixa)
                                                                                </span>
                                                                                <span style={{ fontWeight: 'bold', color: '#d32f2f' }}>
                                                                                    • ✅ <strong>Recomendação:</strong> Aumente a quantidade para <strong>{warning.recommendedQuantity}</strong> unidades
                                                                                    ({warning.boxesToComplete} caixas completas)
                                                                                </span>
                                                                                <span style={{ color: '#666', fontSize: '11px' }}>
                                                                                    • Faltam apenas <strong>{warning.additionalUnitsNeeded}</strong> unidades para completar a próxima caixa
                                                                                </span>
                                                                                <button
                                                                                    type="button"
                                                                                    onClick={() => {
                                                                                        const newQuantity = warning.recommendedQuantity;
                                                                                        const unitPrice = parseFloat(item.unitPrice) || 0;
                                                                                        const newTotal = (newQuantity * unitPrice).toFixed(2);
                                                                                        setOrderItems(prev => prev.map(i =>
                                                                                            i.id === item.id
                                                                                                ? { ...i, quantity: newQuantity.toString(), total: newTotal }
                                                                                                : i
                                                                                        ));
                                                                                        addToast(`Quantidade ajustada para ${newQuantity} unidades (${warning.boxesToComplete} caixas completas)`, 'Success');
                                                                                    }}
                                                                                    className={styles.fixSingleItemButton}
                                                                                    style={{
                                                                                        marginTop: '4px',
                                                                                        padding: '2px 12px',
                                                                                        backgroundColor: '#d32f2f',
                                                                                        color: 'white',
                                                                                        border: 'none',
                                                                                        borderRadius: '4px',
                                                                                        fontSize: '11px',
                                                                                        cursor: 'pointer',
                                                                                        alignSelf: 'flex-start'
                                                                                    }}
                                                                                >
                                                                                    Aplicar recomendação (+{warning.additionalUnitsNeeded} un.)
                                                                                </button>
                                                                            </div>
                                                                        </div>
                                                                    </td>
                                                                </tr>
                                                            )}

                                                            {/* Warning message row for regular warnings */}
                                                            {hasWarning && severity === 'warning' && (
                                                                <tr className={styles.warningMessageRow}>
                                                                    <td colSpan="8" style={{
                                                                        padding: '4px 20px',
                                                                        backgroundColor: '#fff8e1',
                                                                        borderLeft: '4px solid #ffc107'
                                                                    }}>
                                                                        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', fontSize: '12px', color: '#856404', flexDirection: 'column' }}>
                                                                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                                                <span>⚠️</span>
                                                                                <span>
                                                                                    <strong>Sugestão:</strong> A quantidade atual deixa {warning.remainingUnits.toFixed(2)} unidades
                                                                                    ({warning.remainingPercentage.toFixed(0)}% da caixa) sobrando.
                                                                                </span>
                                                                            </div>
                                                                            <div style={{ paddingLeft: '28px' }}>
                                                                                <span>
                                                                                    💡 Considere aumentar para <strong>{warning.recommendedQuantity}</strong> unidades
                                                                                    ({warning.boxesToComplete} caixas completas) - faltam apenas <strong>{warning.additionalUnitsNeeded}</strong> unidades.
                                                                                </span>
                                                                                <button
                                                                                    type="button"
                                                                                    onClick={() => {
                                                                                        const newQuantity = warning.recommendedQuantity;
                                                                                        const unitPrice = parseFloat(item.unitPrice) || 0;
                                                                                        const newTotal = (newQuantity * unitPrice).toFixed(2);
                                                                                        setOrderItems(prev => prev.map(i =>
                                                                                            i.id === item.id
                                                                                                ? { ...i, quantity: newQuantity.toString(), total: newTotal }
                                                                                                : i
                                                                                        ));
                                                                                        addToast(`Quantidade ajustada para ${newQuantity} unidades (${warning.boxesToComplete} caixas completas)`, 'Success');
                                                                                    }}
                                                                                    className={styles.fixSingleItemButton}
                                                                                    style={{
                                                                                        marginTop: '4px',
                                                                                        marginLeft: '8px',
                                                                                        padding: '2px 12px',
                                                                                        backgroundColor: '#ffc107',
                                                                                        color: '#333',
                                                                                        border: 'none',
                                                                                        borderRadius: '4px',
                                                                                        fontSize: '11px',
                                                                                        cursor: 'pointer'
                                                                                    }}
                                                                                >
                                                                                    Aplicar (+{warning.additionalUnitsNeeded} un.)
                                                                                </button>
                                                                            </div>
                                                                        </div>
                                                                    </td>
                                                                </tr>
                                                            )}

                                                            {/* NCM summary row */}
                                                            {ncmConfig && (
                                                                <tr className={styles.ncmSummaryRow}>
                                                                    <td colSpan="8" style={{ padding: '4px 12px' }}>
                                                                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', fontSize: '10px', color: '#666' }}>
                                                                            <span><strong>NCM:</strong> {item.ncmCode}</span>
                                                                            <span><strong>ST:</strong> {ncmConfig.situacaoTributaria || '00'}</span>
                                                                            <span><strong>ICMS:</strong> {ncmConfig.aliquotaIcmsProprio || 0}%</span>
                                                                            <span><strong>ICMS ST:</strong> {ncmConfig.aliquotaIcmsSTOutraUF || 0}%</span>
                                                                            <span><strong>IPI:</strong> {ncmConfig.aliquotaIpiSaida || 0}%</span>
                                                                            <span><strong>PIS:</strong> {ncmConfig.aliquotaPISSaida || 0}%</span>
                                                                            <span><strong>COFINS:</strong> {ncmConfig.aliquotaCofinsSaida || 0}%</span>
                                                                            <span><strong>CBS:</strong> {ncmConfig.aliquotaCBS || 0}%</span>
                                                                            <span><strong>IBS:</strong> {ncmConfig.aliquotaIBS || 0}%</span>
                                                                        </div>
                                                                    </td>
                                                                </tr>
                                                            )}
                                                        </React.Fragment>
                                                    );
                                                })
                                            )}
                                        </tbody>
                                        <tfoot>
                                            {/* Box completion warning summary */}
                                            {Object.keys(boxCompletionWarnings).length > 0 && (
                                                <tr className={styles.warningSummaryRow}>
                                                    <td colSpan="8" style={{
                                                        padding: '6px 12px',
                                                        backgroundColor: '#fff8e1',
                                                        borderTop: '2px solid #ffc107'
                                                    }}>
                                                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                                                            <span style={{ fontSize: '16px' }}>⚠️</span>
                                                            <span style={{ fontWeight: 'bold', color: '#856404' }}>
                                                                {Object.keys(boxCompletionWarnings).length} produto(s) com quantidade não preenche caixas completas:
                                                            </span>
                                                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                                                                {Object.entries(boxCompletionWarnings).map(([itemId, warning]) => {
                                                                    const item = orderItems.find(i => i.id === itemId);
                                                                    return item ? (
                                                                        <span key={itemId} style={{
                                                                            marginLeft: '4px',
                                                                            display: 'inline-block',
                                                                            backgroundColor: '#fff3cd',
                                                                            padding: '2px 8px',
                                                                            borderRadius: '4px',
                                                                            fontSize: '11px'
                                                                        }}>
                                                                            <span style={{ fontWeight: '500' }}>{item.name || item.code}</span>
                                                                            <span style={{ color: '#999' }}> ({warning.completeBoxes} caixas + {warning.remainingUnits.toFixed(2)} un. extras)</span>
                                                                            <span style={{ color: '#d32f2f', fontWeight: 'bold' }}>
                                                                                → {warning.recommendedQuantity} un. ({warning.boxesToComplete} caixas)
                                                                            </span>
                                                                        </span>
                                                                    ) : null;
                                                                })}
                                                            </div>
                                                            <button
                                                                type="button"
                                                                onClick={handleAutoFixQuantities}
                                                                className={styles.fixQuantitiesButton}
                                                                style={{ fontSize: '11px', padding: '2px 10px' }}
                                                            >
                                                                <FiMaximize size={12} /> Aplicar Todos
                                                            </button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            )}
                                            <tr className={styles.tableFooterRow}>
                                                <td colSpan="6" className={styles.labelRight}>
                                                    <strong>Qtde de itens no PV:</strong>
                                                </td>
                                                <td className={styles.valueCell}>
                                                    {orderItems.length}
                                                </td>
                                                <td colSpan="2"></td>
                                            </tr>
                                        </tfoot>
                                    </table>
                                </div>
                            </div>
                        </div>

                        {/* SEÇÃO 3: CONDIÇÕES COMERCIAIS */}
                        <div className={styles.formSection}>
                            <div className={styles.sectionHeader}>
                                <div className={styles.sectionIcon}><FiDollarSign size={18} /></div>
                                <h3 className={styles.sectionTitle}>Condições Comerciais</h3>
                                <span className={styles.sectionBadge}>Baseado no NCM do produto</span>
                            </div>
                            <div className={styles.formGrid}>
                                <div className={styles.inputGroup}>
                                    <label>Valor Prev. ICMS (R$)</label>
                                    <input
                                        type="number"
                                        step="0.01"
                                        value={estimatedIcms}
                                        onChange={(e) => setEstimatedIcms(e.target.value)}
                                        className={styles.input}
                                        disabled={formLoading}
                                        placeholder="0,00"
                                    />
                                    <small className={styles.fieldHint}>Auto-preenchido pelo NCM</small>
                                </div>

                                <div className={styles.inputGroup}>
                                    <label>Valor Prev. ICMS ST (R$)</label>
                                    <input type="number"
                                        step="0.01"
                                        value={estimatedIcmsSt}
                                        onChange={(e) => setEstimatedIcmsSt(e.target.value)}
                                        className={styles.input}
                                        disabled={formLoading}
                                        placeholder="0,00"
                                    />
                                    <small className={styles.fieldHint}>Auto-preenchido pelo NCM</small>
                                </div>

                                <div className={styles.inputGroup}>
                                    <label>Valor Prev. FCP ST (R$)</label>
                                    <input
                                        type="number"
                                        step="0.01"
                                        value={estimatedFcpSt}
                                        onChange={(e) => setEstimatedFcpSt(e.target.value)}
                                        className={styles.input}
                                        disabled={formLoading}
                                        placeholder="0,00"
                                    />
                                </div>

                                <div className={styles.inputGroup}>
                                    <label>Valor Prev. IPI (R$)</label>
                                    <input
                                        type="number"
                                        step="0.01"
                                        value={estimatedIpi}
                                        onChange={(e) => setEstimatedIpi(e.target.value)}
                                        className={styles.input}
                                        disabled={formLoading}
                                        placeholder="0,00"
                                    />
                                    <small className={styles.fieldHint}>Auto-preenchido pelo NCM</small>
                                </div>

                                <div className={styles.inputGroup}>
                                    <label>Valor Total dos Produtos (R$)</label>
                                    <input
                                        type="text"
                                        value={`R$ ${parseFloat(calculatedValues.itemsTotal).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`}
                                        className={styles.input}
                                        disabled
                                        style={{ backgroundColor: '#f0f0f0', fontWeight: 'bold', color: 'var(--primary-color)' }}
                                    />
                                </div>

                                <div className={styles.inputGroup}>
                                    <label>Valor Total Bonificado (R$)</label>
                                    <input
                                        type="text"
                                        value={`R$ ${parseFloat(calculatedValues.bonusTotal).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`}
                                        className={styles.input}
                                        disabled
                                        style={{ backgroundColor: '#f0f0f0' }}
                                    />
                                </div>

                                <div className={styles.inputGroup}>
                                    <label>Comissão</label>
                                    <div style={{ display: 'flex', gap: '8px' }}>
                                        <select
                                            value={commissionType}
                                            onChange={(e) => setCommissionType(e.target.value)}
                                            className={styles.select}
                                            style={{ width: '100px' }}
                                            disabled={formLoading}
                                        >
                                            <option value="percent">%</option>
                                            <option value="fixed">R$</option>
                                        </select>
                                        {commissionType === 'percent' ? (
                                            <input
                                                type="number"
                                                step="0.01"
                                                value={commissionPercent}
                                                onChange={(e) => setCommissionPercent(e.target.value)}
                                                className={styles.input}
                                                disabled={formLoading}
                                                placeholder="0,00"
                                                style={{ flex: 1 }}
                                            />
                                        ) : (
                                            <input
                                                type="number"
                                                step="0.01"
                                                value={commissionValue}
                                                onChange={(e) => setCommissionValue(e.target.value)}
                                                className={styles.input}
                                                disabled={formLoading}
                                                placeholder="0,00"
                                                style={{ flex: 1 }}
                                            />
                                        )}
                                    </div>
                                    <small className={styles.fieldHint}>
                                        Taxa de comissão sobre o valor total dos produtos
                                    </small>
                                </div>

                                <div className={styles.inputGroup}>
                                    <label>Valor Total da Comissão (R$)</label>
                                    <input
                                        type="text"
                                        value={`R$ ${parseFloat(calculatedValues.commissionTotal).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`}
                                        className={styles.input}
                                        disabled
                                        style={{ backgroundColor: '#f0f0f0', fontWeight: 'bold', color: 'var(--success-color)' }}
                                    />
                                </div>

                                <div className={styles.inputGroup} style={{ gridColumn: 'span 2' }}>
                                    <label>Observações Comerciais</label>
                                    <textarea
                                        value={commercialNotes}
                                        onChange={(e) => setCommercialNotes(e.target.value)}
                                        className={styles.textarea}
                                        rows={3}
                                        placeholder="Observações comerciais..."
                                        disabled={formLoading}
                                    />
                                </div>
                            </div>
                        </div>

                        {/* SEÇÃO 4: RESUMO DE VALORES */}
                        <div className={styles.formSection}>
                            <div className={styles.sectionHeader}>
                                <div className={styles.sectionIcon}><FiTrendingUp size={18} /></div>
                                <h3 className={styles.sectionTitle}>Resumo de Valores</h3>
                            </div>
                            <div className={styles.resumoGrid}>
                                <div className={styles.resumoLinha}>
                                    <span className={styles.resumoLabel}>Subtotal:</span>
                                    <span className={styles.resumoValor}>$ {calculatedValues.itemsTotal}</span>
                                </div>
                                <div className={styles.resumoLinha}>
                                    <span className={styles.resumoLabel}>Desconto Cliente:</span>
                                    <span className={styles.resumoValor}>- R$ {calculatedValues.discountAmount}</span>
                                </div>
                                <div className={styles.resumoLinha}>
                                    <span className={styles.resumoLabel}>Desconto Boleto:</span>
                                    <span className={styles.resumoValor}>- R$ {calculatedValues.ticketDiscountAmount}</span>
                                </div>
                                <div className={styles.resumoLinha}>
                                    <span className={styles.resumoLabel}>ICMS:</span>
                                    <span className={styles.resumoValor}>R$ {calculatedValues.icms}</span>
                                </div>
                                <div className={styles.resumoLinha}>
                                    <span className={styles.resumoLabel}>ICMS ST:</span>
                                    <span className={styles.resumoValor}>R$ {calculatedValues.icmsSt}</span>
                                </div>
                                <div className={styles.resumoLinha}>
                                    <span className={styles.resumoLabel}>FCP ST:</span>
                                    <span className={styles.resumoValor}>R$ {calculatedValues.fcpSt}</span>
                                </div>
                                <div className={styles.resumoLinha}>
                                    <span className={styles.resumoLabel}>IPI:</span>
                                    <span className={styles.resumoValor}>R$ {calculatedValues.ipi}</span>
                                </div>
                                <div className={styles.resumoLinha}>
                                    <span className={styles.resumoLabel}>Comissão:</span>
                                    <span className={styles.resumoValor}>R$ {calculatedValues.commissionTotal}</span>
                                </div>
                                <div className={`${styles.resumoLinha} ${styles.resumoTotal}`}>
                                    <span className={styles.resumoLabel}>Valor Total R$:</span>
                                    <span className={styles.resumoValorTotal}>R$ {calculatedValues.total}</span>
                                </div>
                            </div>
                        </div>

                        {/* SEÇÃO 5: PLANO RECEBIMENTO */}
                        <div className={styles.formSection}>
                            <div className={styles.sectionHeader}>
                                <div className={styles.sectionIcon}><FiCreditCard size={18} /></div>
                                <h3 className={styles.sectionTitle}>Plano Recebimento</h3>
                            </div>
                            <div className={styles.formGrid}>
                                <div className={styles.inputGroup}>
                                    <label>Forma de Pagamento</label>
                                    <select
                                        value={selectedPaymentMethodId}
                                        onChange={(e) => setSelectedPaymentMethodId(e.target.value)}
                                        className={styles.select}
                                        disabled={formLoading || paymentMethodsLoading}
                                    >
                                        <option value="">Selecione...</option>
                                        {paymentMethodOptions.map(opt => (
                                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                                        ))}
                                    </select>
                                </div>

                                <div className={styles.inputGroup}>
                                    <label>Condição de Fat.</label>
                                    <select
                                        value={selectedBillingConditionId}
                                        onChange={(e) => setSelectedBillingConditionId(e.target.value)}
                                        className={styles.select}
                                        disabled={formLoading || billingConditionsLoading}
                                    >
                                        <option value="">Selecione...</option>
                                        {billingConditionOptions.map(opt => (
                                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                                        ))}
                                    </select>
                                </div>
                            </div>
                        </div>

                        {/* SEÇÃO 6: PLANO ENTREGA */}
                        <div className={styles.formSection}>
                            <div className={styles.sectionHeader}>
                                <div className={styles.sectionIcon}><FiTruck size={18} /></div>
                                <h3 className={styles.sectionTitle}>Plano Entrega</h3>
                            </div>
                            <div className={styles.formGrid}>
                                <div className={styles.inputGroup} style={{ maxWidth: '300px' }}>
                                    <label>Data Entrega</label>
                                    <input
                                        type="text"
                                        value={deliveryDateDisplay}
                                        onChange={handleDeliveryDateChange}
                                        className={styles.input}
                                        disabled={formLoading}
                                        placeholder="DD/MM/AAAA"
                                    />
                                    <small className={styles.fieldHint}>Data prevista para entrega (formato DD/MM/AAAA)</small>
                                </div>
                            </div>
                        </div>

                        {/* SEÇÃO 7: DADOS PARA PRODUÇÃO */}
                        <div className={styles.formSection}>
                            <div className={styles.sectionHeader}>
                                <div className={styles.sectionIcon}><FiBox size={18} /></div>
                                <h3 className={styles.sectionTitle}>Dados para Produção</h3>
                            </div>
                            <div className={styles.formGrid}>
                                <div className={styles.inputGroup} style={{ gridColumn: 'span 2' }}>
                                    <label>Observações para produção</label>
                                    <textarea
                                        value={productionNotes}
                                        onChange={(e) => setProductionNotes(e.target.value)}
                                        className={styles.textarea}
                                        rows={4}
                                        placeholder="Instruções específicas para a produção..."
                                        disabled={formLoading}
                                    />
                                    <small className={styles.fieldHint}>
                                        Informações adicionais para o setor de produção
                                    </small>
                                </div>
                            </div>
                        </div>

                        {/* Botões de Ação */}
                        <div className={styles.formActions}>
                            <button
                                type="button"
                                onClick={resetForm}
                                className={styles.secondaryButton}
                                disabled={formLoading}
                            >
                                <FiX size={18} /> Cancelar
                            </button>
                            <button
                                type="submit"
                                className={styles.primaryButton}
                                disabled={formLoading}
                            >
                                {formLoading ? (
                                    <>
                                        <FiRefreshCw size={18} className={styles.buttonSpinner} />
                                        Salvando...
                                    </>
                                ) : (
                                    (editingItem && editingItem.id) ? <><FiSave size={18} /> Salvar Alterações</> : <><FiPlus size={18} /> Criar Pedido de Venda</>
                                )}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        );
    };

    // ============================================
    // 2.12 RENDERIZAÇÃO PRINCIPAL
    // ============================================

    return (
        <div className={styles.pageContainer}>
            {/* Toast Container */}
            {toasts.length > 0 && (
                <div className={styles.toastContainer} aria-live="polite">
                    {toasts.map(toast => (
                        <Toast
                            key={toast.id}
                            message={toast.message}
                            type={toast.type}
                            onClose={() => setToasts(prev => prev.filter(t => t.id !== toast.id))}
                        />
                    ))}
                </div>
            )}

            {/* Delete Modal */}
            {deleteModal && (
                <DeleteModal
                    isOpen={deleteModal.isOpen}
                    title={deleteModal.title}
                    message={deleteModal.message}
                    itemName={deleteModal.itemName}
                    itemCode={deleteModal.itemCode}
                    confirmText={deleteModal.confirmText}
                    cancelText={deleteModal.cancelText}
                    onConfirm={deleteModal.onConfirm}
                    onCancel={deleteModal.onCancel}
                    isLoading={deleteModal.isLoading}
                />
            )}

            {renderOrderModal()}

            {/* Modals */}
            {isViewModalOpen && (
                <ViewOrderModal
                    order={selectedOrder}
                    onClose={() => { setIsViewModalOpen(false); setSelectedOrder(null); }}
                />
            )}

            {isConfirmModalOpen && (
                <ConfirmOrderModal
                    order={selectedOrder}
                    onConfirm={handleConfirmOrder}
                    onClose={() => { setIsConfirmModalOpen(false); setSelectedOrder(null); }}
                    isLoading={confirmOrderLoading}
                />
            )}

            {isTrackingModalOpen && (
                <TrackingModal
                    order={selectedOrder}
                    onClose={() => { setIsTrackingModalOpen(false); setSelectedOrder(null); }}
                    onUpdateStatus={handleUpdateOrderStatus}
                    addToast={addToast}
                />
            )}

            {/* Header */}
            <PageHeader
                title="Pedidos de Venda"
                actionText={can.create ? "Novo Pedido" : ""}
                onAction={handleNewClick}
                disabled={ordersLoading}
                showAction={can.create}
            />

            {/* Search Bar with Status Filter */}
            <div className={styles.searchBar}>
                <div className={styles.searchBarWithFilter}>
                    <div className={styles.searchBarInput}>
                        <SearchBar
                            placeholder="Código do pedido, cliente..."
                            onSearch={(value) => {
                                setSearchTerm(value);
                                setCurrentPage(1);
                            }}
                            initialValue={searchTerm}
                            debounceDelay={300}
                            disabled={formLoading}
                        />
                    </div>
                    <div className={styles.filterSelectWrapper}>
                        <select
                            className={styles.filterSelect}
                            value={statusFilter}
                            onChange={(e) => {
                                setStatusFilter(e.target.value);
                                setCurrentPage(1);
                            }}
                            disabled={formLoading}
                        >
                            <option value="all">Todos os Status</option>
                            <option value="draft">Rascunho</option>
                            <option value="pending">Pendentes</option>
                            <option value="approved">Aprovados</option>
                            <option value="cancelled">Cancelados</option>
                            <option value="returned">Retornados para Análise</option>
                        </select>
                    </div>
                </div>
            </div>

            {/* Table */}
            <div className={styles.tableContainer}>
                <table className={styles.table}>
                    <thead>
                        <tr>
                            <th>Código</th>
                            <th>Cliente</th>
                            <th>Data</th>
                            <th style={{ textAlign: 'right' }}>Valor Total</th>
                            <th>Status</th>
                            <th style={{ width: '310px' }}>Ações</th>
                        </tr>
                    </thead>
                    <tbody>
                        {showSkeleton ? (
                            <tr>
                                <td colSpan="6">
                                    <TableSkeleton columns={6} rows={ITEMS_PER_PAGE} />
                                </td>
                            </tr>
                        ) : currentOrders && currentOrders.length > 0 ? (
                            currentOrders.map(order => {
                                // Check if order can be edited/deleted (DRAFT or RETURNED_FOR_ANALYSIS)
                                const canEditDelete = order.status === ORDER_STATUS.DRAFT ||
                                    order.status === ORDER_STATUS.RETURNED_FOR_ANALYSIS;
                                return (
                                    <tr key={order.id} className={styles.tableRow}>
                                        <td><strong>{order.code}</strong></td>
                                        <td>{order.clientName}</td>
                                        <td>{order.createdAt?.toDate ? order.createdAt.toDate().toLocaleDateString('pt-BR') : new Date(order.createdAt).toLocaleDateString('pt-BR')}</td>
                                        <td className={styles.priceCell} style={{ textAlign: 'right' }}>$ {order.calculatedValues?.total?.toLocaleString('pt-BR', { minimumFractionDigits: 2 }) || '0,00'}</td>
                                        <td>
                                            <StatusBadge status={order.status} />
                                        </td>
                                        <td>
                                            <div className={styles.actionsContainer}>
                                                {can.view && (
                                                    <button
                                                        onClick={() => handleViewClick(order)}
                                                        className={`${styles.actionButton} ${styles.viewButton}`}
                                                        disabled={formLoading}
                                                        title="Visualizar"
                                                    >
                                                        <FiEye size={14} />
                                                    </button>
                                                )}

                                                {can.approve && order.status === ORDER_STATUS.DRAFT && (
                                                    <button
                                                        onClick={() => handleConfirmClick(order)}
                                                        className={`${styles.actionButton} ${styles.confirmButton}`}
                                                        disabled={formLoading}
                                                        title="Confirmar e enviar para aprovação"
                                                    >
                                                        <FiCheck size={14} />
                                                    </button>
                                                )}

                                                {can.track && (
                                                    <button
                                                        onClick={() => handleTrackingClick(order)}
                                                        className={`${styles.actionButton} ${styles.trackButton}`}
                                                        disabled={formLoading}
                                                        title="Rastrear"
                                                    >
                                                        <FiMapPin size={14} />
                                                    </button>
                                                )}
                                                {can.print && (
                                                    <button
                                                        onClick={() => printOrder(order)}
                                                        className={`${styles.actionButton} ${styles.paymentButton}`}
                                                        disabled={formLoading}
                                                        title="Imprimir"
                                                    >
                                                        <FiPrinter size={14} />
                                                    </button>
                                                )}

                                                {can.edit && (
                                                    <button
                                                        onClick={() => handleEditClick(order)}
                                                        className={`${styles.actionButton} ${styles.editButton}`}
                                                        disabled={formLoading || !canEditDelete}
                                                        title={!canEditDelete
                                                            ? "Apenas pedidos em rascunho ou retornados para análise podem ser editados"
                                                            : "Editar"}
                                                    >
                                                        <FiEdit size={14} />
                                                    </button>
                                                )}

                                                {can.delete && (
                                                    <button
                                                        onClick={() => handleDelete(order)}
                                                        className={`${styles.actionButton} ${styles.deleteButton}`}
                                                        disabled={formLoading || !canEditDelete}
                                                        title={!canEditDelete
                                                            ? "Apenas pedidos em rascunho ou retornados para análise podem ser excluídos"
                                                            : "Excluir"}
                                                    >
                                                        <FiTrash2 size={14} />
                                                    </button>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })
                        ) : (
                            <tr>
                                <td colSpan="6">
                                    <EmptyState
                                        title={searchTerm || statusFilter !== 'all' ? "Nenhum pedido encontrado com os filtros aplicados." : "Nenhum pedido de venda encontrado."}
                                        message={searchTerm || statusFilter !== 'all' ? "Tente ajustar os filtros de busca." : "Comece criando um novo pedido de venda."}
                                    />
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            {/* Pagination */}
            {!showSkeleton && totalPages > 1 && (
                <Pagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    totalItems={filteredOrders.length}
                    onPageChange={handlePageChange}
                    disabled={formLoading}
                    itemsLabel="pedidos"
                />
            )}
        </div>
    );
};

export default SaleOrderPage;